import React from 'react'
import {
  Box,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material'
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded'
import BlockRoundedIcon from '@mui/icons-material/BlockRounded'
import ContentCutRoundedIcon from '@mui/icons-material/ContentCutRounded'
import HourglassBottomRoundedIcon from '@mui/icons-material/HourglassBottomRounded'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded'
import SpeedRoundedIcon from '@mui/icons-material/SpeedRounded'
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded'
import {
  deletePlanificacion,
  getGruposConfeccion,
  getMisColoresMaquinas,
  getMisColoresVariedades,
  getPedidosLineasPendientes,
  getParametrosTiempo,
  getPlanificacionesTablero,
  getSalidasProduccion,
  getSession,
  getTiposMaquinas,
  patchPlanificacion,
  postPlanificacion,
} from '../services/apis'
import { LoadingMaestro } from '../components/LoadingMaestro'
import { normalizarArray, normalizarPaginacion } from '../utils/apiResponse'
import { socket } from '../api/socket'
import {
  ALMACEN_SELECCIONADO_EVENT,
  ALMACEN_SELECCIONADO_STORAGE_KEY,
  getAlmacenSeleccionado,
} from '../utils/almacenSeleccionado'
import { getUsuarioFromSession, puedeEditarPlanificacion } from '../utils/permisos'
import { PlannerTopBar } from '../components/planner/PlannerTopBar'
import { PedidosPendientesPanel } from '../components/planner/PedidosPendientesPanel'
import { SplitPlanificacionDialog } from '../components/planner/SplitPlanificacionDialog'
import { PedidoDetalleDialog } from '../components/planner/PedidoDetalleDialog'
import { PlannerErrorSnackbar } from '../components/planner/PlannerErrorSnackbar'
import { VariedadLabel } from '../components/planner/VariedadLabel'

const MS_PER_HOUR = 60 * 60 * 1000
const DEFAULT_VISIBLE_HOURS = 24
const MIN_VISIBLE_HOURS = 12
const MAX_VISIBLE_HOURS = 72
const ZOOM_STEP_HOURS = 12
const HOUR_CELL_WIDTH = 640
const QUARTER_HOUR_CELL_WIDTH = HOUR_CELL_WIDTH / 4
const LABEL_WIDTH = 210
const ROW_HEIGHT = 98
const TASK_CARD_HEIGHT = 78
const TASK_CARD_TOP = 10
const COMPACT_ROW_HEIGHT = 42
const COMPACT_TASK_CARD_HEIGHT = 28
const COMPACT_TASK_CARD_TOP = 7
const MIN_TASK_CARD_WIDTH = 154
const DRAG_DATA_TYPE = 'application/citrus-planner'
const DROP_SNAP_MINUTES = 5
const CURRENT_TIME_SCROLL_LEAD_PX = 32
const CAMBIOS_VISTOS_STORAGE_KEY = 'citrusPlannerCambiosVistos'
const TOUCH_DRAG_THRESHOLD_PX = 12

const hourFormatter = new Intl.DateTimeFormat('es-ES', {
  hour: '2-digit',
  minute: '2-digit',
})

const dayFormatter = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: 'short',
})

const dateTimeFormatter = new Intl.DateTimeFormat('es-ES', {
  day: '2-digit',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
})

const formatDateTime = (date) => {
  if (!date) return '-'

  const parsedDate = new Date(date)

  return Number.isNaN(parsedDate.getTime()) ? '-' : dateTimeFormatter.format(parsedDate)
}

const formatTime = (date) => {
  if (!date) return '-'

  const parsedDate = new Date(date)

  return Number.isNaN(parsedDate.getTime()) ? '-' : hourFormatter.format(parsedDate)
}

const toDateInputValue = (date = new Date()) => {
  const parsedDate = date instanceof Date ? date : new Date(date)

  if (Number.isNaN(parsedDate.getTime())) return ''

  const year = parsedDate.getFullYear()
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0')
  const day = String(parsedDate.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const addDaysToDateValue = (dateValue, days) => {
  const baseDate = new Date(`${dateValue || toDateInputValue()}T12:00:00`)

  if (Number.isNaN(baseDate.getTime())) return toDateInputValue()

  baseDate.setDate(baseDate.getDate() + days)

  return toDateInputValue(baseDate)
}

const statusStyles = {
  completed: {
    background: 'linear-gradient(135deg, #FFFFFF 0%, #DCFCE7 100%)',
    border: '1px solid #86EFAC',
    shadow: '2px 3px 3px rgba(22, 163, 74, 0.14)',
    titleColor: '#14532D',
    metaColor: '#15803D',
    noteColor: '#5C7091',
    badgeBg: '#FFFFFF',
    badgeColor: '#15803D',
    progressTrack: 'rgba(22, 163, 74, 0.14)',
    progressFill: '#16A34A',
  },
  active: {
    background: 'linear-gradient(135deg, #FF9B22 0%, #F57C00 100%)',
    border: '1px solid #F57C00',
    shadow: '2px 3px 3px rgba(245, 124, 0, 0.28)',
    titleColor: '#FFFFFF',
    metaColor: 'rgba(255, 255, 255, 0.92)',
    noteColor: 'rgba(255, 255, 255, 0.82)',
    badgeBg: 'rgba(255, 255, 255, 0.18)',
    badgeColor: '#FFFFFF',
    progressTrack: 'rgba(255, 255, 255, 0.18)',
    progressFill: '#FFFFFF',
  },
  delayed: {
    background: 'linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%)',
    border: '1px solid #FCD34D',
    shadow: '2px 3px 3px rgba(180, 83, 9, 0.14)',
    titleColor: '#713F12',
    metaColor: '#B45309',
    noteColor: '#92400E',
    badgeBg: '#FFFFFF',
    badgeColor: '#B45309',
    progressTrack: 'rgba(180, 83, 9, 0.14)',
    progressFill: '#F59E0B',
  },
  waiting: {
    background: 'linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%)',
    border: '1px solid #93C5FD',
    shadow: '2px 3px 3px rgba(37, 99, 235, 0.10)',
    titleColor: '#1E3A8A',
    metaColor: '#1D4ED8',
    noteColor: '#1E40AF',
    badgeBg: '#FFFFFF',
    badgeColor: '#1D4ED8',
    progressTrack: 'rgba(37, 99, 235, 0.12)',
    progressFill: '#60A5FA',
  },
  cancelled: {
    background: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)',
    border: '1px dashed #F0A8A8',
    shadow: '2px 3px 3px rgba(185, 28, 28, 0.08)',
    titleColor: '#475569',
    metaColor: '#9F4B4B',
    noteColor: '#7F5D5D',
    badgeBg: '#FFFFFF',
    badgeColor: '#991B1B',
    progressTrack: 'rgba(185, 28, 28, 0.10)',
    progressFill: '#F0A8A8',
    cancelBg: '#FEE2E2',
    cancelColor: '#991B1B',
    accent: '#B91C1C',
  },
}

const legendItems = [
  { label: 'Completado', color: '#16A34A' },
  { label: 'Activo', color: '#F57C00' },
  { label: 'Retrasado', color: '#F59E0B' },
  { label: 'En espera', color: statusStyles.waiting.progressFill },
  { label: 'Cancelado BC', color: '#991B1B' },
]

const getPedidoKey = (item) => `${item?.numPedido ?? ''}-${item?.numLinea ?? ''}`

const normalizarClave = (value) => String(value ?? '').trim()

const normalizarNumeroClave = (value) => {
  const normalized = normalizarClave(value).replace(',', '.')
  const number = Number(normalized)

  return Number.isFinite(number) ? String(number) : normalized
}

const asArray = (value) => (Array.isArray(value) ? value : [])

const isHexColor = (value) => /^#[0-9A-F]{6}$/i.test(normalizarClave(value))

const hexToRgba = (hexColor, alpha) => {
  const color = normalizarClave(hexColor)

  if (!isHexColor(color)) return ''

  const red = parseInt(color.slice(1, 3), 16)
  const green = parseInt(color.slice(3, 5), 16)
  const blue = parseInt(color.slice(5, 7), 16)

  return `rgba(${red}, ${green}, ${blue}, ${alpha})`
}

const getColorMaquinaTenue = (colorMaquina, alpha = 0.11) => (
  hexToRgba(colorMaquina, alpha) || '#FFFFFF'
)

const normalizarFiltroGruposConfeccion = (value) => (
  asArray(value)
    .map((grupo) => normalizarClave(grupo))
    .filter((grupo) => grupo && grupo !== 'todos')
)

const getCodMaquinaColorUsuario = (colorMaquinaUsuario) => (
  colorMaquinaUsuario?.codMaquina
  ?? colorMaquinaUsuario?.maquina?.codMaquina
  ?? colorMaquinaUsuario?.salidaProduccion?.codMaquina
  ?? ''
)

const getColorMaquinaUsuario = (colorMaquinaUsuario) => (
  colorMaquinaUsuario?.colorMaquina
  ?? colorMaquinaUsuario?.color
  ?? ''
)

const getConfeccionPedido = (pedido) => (
  pedido?.confeccion
  || pedido?.pedido?.confeccion
  || pedido?.linea?.confeccion
  || pedido?.pedido?.linea?.confeccion
  || {}
)

const getCodGrupoConfeccionPedido = (pedido) => (
  pedido?.codGrupoConfeccion
  || pedido?.confeccion?.codGrupoConfeccion
  || pedido?.confeccionPedido?.codGrupoConfeccion
  || pedido?.pedido?.confeccion?.codGrupoConfeccion
  || pedido?.linea?.confeccion?.codGrupoConfeccion
  || pedido?.pedido?.linea?.confeccion?.codGrupoConfeccion
  || pedido?.linea?.codGrupoConfeccion
  || pedido?.pedido?.linea?.codGrupoConfeccion
  || ''
)

const getPedidoPendienteDesdeTask = (task) => ({
  ...(task?.pedidoRaw || {}),
  numPedido: task?.numPedido,
  numLinea: task?.numLinea,
  desCliente: task?.desCliente,
  desDirEnvio: task?.desDirEnvio,
  desVariedad: task?.desVariedad,
  codVariedad: task?.codVariedad,
  desConfeccion: task?.desConfeccion,
  desCalibre: task?.calibre,
  desMarca: task?.desMarca,
  cantidadPales: Math.max(
    Number(task?.cantidadPales ?? 0),
    Number(task?.pedidoRaw?.cantidadPales ?? 0),
    Number(task?.lineaPedido?.cantidadPales ?? 0),
  ),
  cantidadPlanificada: task?.cantidadPales,
  cantidadPendiente: 0,
  paletsProducidos: task?.paletsProducidosPedido ?? task?.produccion?.paletsProducidos ?? 0,
  pedido: {
    ...(task?.pedidoRaw?.pedido || {}),
    cabecera: task?.cabeceraPedido || task?.pedidoRaw?.pedido?.cabecera,
    linea: task?.lineaPedido || task?.pedidoRaw?.pedido?.linea,
    confeccion: task?.confeccionPedido || task?.pedidoRaw?.pedido?.confeccion,
  },
  linea: task?.lineaPedido,
  confeccion: task?.confeccionPedido,
  __estaPlanificado: true,
  __plannerStatus: task?.status,
  __planificacionTask: task,
})

const getStatusPedidoPlanificado = (tasks, totalPedido, paletsProducidos) => {
  if (!tasks.length) return null

  if (totalPedido > 0 && paletsProducidos >= totalPedido) return 'completed'
  if (tasks.some((task) => task.status === 'cancelled')) return 'cancelled'
  if (tasks.some((task) => task.status === 'delayed')) return 'delayed'
  if (tasks.some((task) => task.status === 'active')) return 'active'

  return 'waiting'
}

const getCodGrupoVariedadPedido = (pedido) => (
  pedido?.codGrupoVariedad
  || pedido?.linea?.codGrupoVariedad
  || pedido?.pedido?.linea?.codGrupoVariedad
  || pedido?.pedido?.codGrupoVariedad
  || ''
)

const getPesoPedido = (pedido) => (
  pedido?.peso
  ?? pedido?.linea?.peso
  ?? pedido?.pedido?.linea?.peso
  ?? getConfeccionPedido(pedido)?.peso
  ?? ''
)

const parametroTiempoCoincideConPedido = (parametro, pedido, codGrupoConfeccionFiltro) => {
  const grupoParametro = normalizarClave(parametro?.codGrupoConfeccion)
  const grupoPedido = normalizarClave(getCodGrupoConfeccionPedido(pedido) || codGrupoConfeccionFiltro)
  const grupoFiltro = normalizarClave(codGrupoConfeccionFiltro)

  if (!grupoParametro) return false
  if (grupoFiltro && grupoFiltro !== 'todos' && grupoParametro !== grupoFiltro) return false
  if (grupoPedido && grupoParametro !== grupoPedido) return false

  const grupoVariedadParametro = normalizarClave(parametro?.codGrupoVariedad)
  const grupoVariedadPedido = normalizarClave(getCodGrupoVariedadPedido(pedido))

  if (grupoVariedadParametro && grupoVariedadPedido && grupoVariedadParametro !== grupoVariedadPedido) {
    return false
  }

  const pesoParametro = normalizarNumeroClave(parametro?.peso)
  const pesoPedido = normalizarNumeroClave(getPesoPedido(pedido))

  if (pesoParametro && pesoPedido && pesoParametro !== pesoPedido) {
    return false
  }

  return true
}

const getParametrosTiempoMaquina = (maquina, salida) => ([
  ...asArray(maquina?.parametrosTiempo),
  ...asArray(maquina?.parametros_tiempo),
  ...asArray(maquina?.parametros),
  ...asArray(salida?.parametrosTiempo),
  ...asArray(salida?.parametros_tiempo),
  ...asArray(salida?.parametros),
])

const isEliminadoBusinessCentral = (item) => Boolean(
  item?.eliminadoBusinessCentral
  || item?.pedido?.eliminadoBusinessCentral
  || item?.pedido?.linea?.eliminadoBusinessCentral
  || item?.linea?.eliminadoBusinessCentral
  || item?.lineaPedido?.eliminadoBusinessCentral
)

const getCantidadPales = (item) => Number(item?.cantidadPales ?? item?.cantidadPalets ?? item?.cantidadPendiente ?? 0)
const getPaletsPlanificados = (item) => Number(item?.cantidadPlanificada ?? item?.paletsPlanificados ?? item?.produccion?.paletsPlanificados ?? 0)
const getPaletsProducidos = (item) => Number(item?.paletsProducidos ?? item?.produccion?.paletsProducidos ?? 0)
const getPaletsPendientesPlanificar = (item) => {
  const pendiente = Number(item?.cantidadPendiente ?? item?.paletsPendientesPlanificar ?? Number.NaN)

  if (Number.isFinite(pendiente)) return Math.max(pendiente, 0)

  return Math.max(getCantidadPales(item) - getPaletsPlanificados(item), 0)
}

const hasPaletProduccionExplicita = (palet) => {
  const estado = String(palet?.estado ?? '').toUpperCase()

  return Boolean(
    palet?.fechaReal
    || palet?.numPaletReal
    || ['COMPLETADO', 'FINALIZADO', 'PRODUCIDO', 'HECHO'].includes(estado)
  )
}

const getProduccionPlanificacion = (planificacion) => {
  const cantidad = getCantidadPales(planificacion)
  const resumen = planificacion.produccionPlanificacion
  const paletsProducidos = Math.max(0, Math.min(cantidad, Number(
    resumen?.paletsProducidos
    ?? (Array.isArray(planificacion.palets) ? planificacion.palets.filter(hasPaletProduccionExplicita).length : 0)
  )))

  return {
    paletsProducidos,
    paletsPendientes: Math.max(cantidad - paletsProducidos, 0),
    porcentajeProducido: cantidad > 0 ? (paletsProducidos / cantidad) * 100 : 0,
    finalizado: cantidad > 0 && paletsProducidos >= cantidad,
  }
}

const getMinutesBetween = (start, end) => {
  const startDate = new Date(start)
  const endDate = new Date(end)

  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null

  return (endDate.getTime() - startDate.getTime()) / (60 * 1000)
}

const getFechaFinRealPalet = (palet) => (
  palet?.fechaFinReal || palet?.fechaRealFin || palet?.fechaReal
)

const getMinutosPrevistosPalet = (palet) => {
  const minutos = Number(
    palet?.minutosEstimados
    ?? palet?.minutosTeoricos
    ?? palet?.minutosPrevistos
  )

  if (Number.isFinite(minutos)) return minutos

  return getMinutesBetween(getFechaInicioPalet(palet), getFechaFinPalet(palet))
}

const getFechaFinPrevistaCalculadaPalet = (palet) => {
  const fechaInicio = getFechaInicioPalet(palet)
  const minutosPrevistos = getMinutosPrevistosPalet(palet)
  const inicioDate = new Date(fechaInicio)

  if (!fechaInicio || Number.isNaN(inicioDate.getTime()) || !Number.isFinite(Number(minutosPrevistos))) {
    return getFechaFinPalet(palet)
  }

  return new Date(inicioDate.getTime() + (Number(minutosPrevistos) * 60 * 1000)).toISOString()
}

const getPaletsConInicioRealEncadenado = (palets, fechaInicioPlanificacion) => (
  palets.map((palet, index) => {
    const paletAnterior = index > 0 ? palets[index - 1] : null
    const fechaFinReal = getFechaFinRealPalet(palet)
    const fechaInicioReal = palet.fechaInicioReal
      || palet.fechaRealInicio
      || (paletAnterior ? getFechaFinRealPalet(paletAnterior) : null)
      || fechaInicioPlanificacion

    return {
      ...palet,
      fechaInicioRealEncadenada: fechaFinReal ? fechaInicioReal : null,
    }
  })
)

const getPaletTooltip = (palet) => {
  const fechaInicio = getFechaInicioPalet(palet)
  const finPrevisto = getFechaFinPrevistaCalculadaPalet(palet)
  const finReal = getFechaFinRealPalet(palet)
  const diferencia = getDiferenciaFinPalet(palet)
  const minutosPrevistos = getMinutosPrevistosPalet(palet)
  const minutosReales = getMinutesBetween(fechaInicio, finReal)
    ?? palet?.minutosReales

  return [
    `Palet ${palet?.numeroPalet ?? '-'}`,
    `Fecha inicio: ${formatDateTime(fechaInicio)}`,
    `Fin previsto: ${formatDateTime(finPrevisto)}`,
    `Minutos previsto: ${formatMinutesSeconds(minutosPrevistos)}`,
    `Fin real: ${formatDateTime(finReal)}`,
    `Minutos real: ${formatMinutesSeconds(minutosReales)}`,
    `Diferencia: ${formatSignedMinutesSeconds(diferencia)}`,
  ].join('\n')
}

const getPaletPendienteTooltip = (palet) => ([
  `Palet ${palet?.numeroPalet ?? '-'}`,
  `Fecha prevista de finalizacion: ${formatDateTime(getFechaFinPrevistaCalculadaPalet(palet))}`,
].join('\n'))

const getPaletBoundsPercent = (palet, task) => {
  if (Number.isFinite(palet?.timelineLeft) && Number.isFinite(palet?.timelineWidth)) {
    return {
      left: palet.timelineLeft,
      width: palet.timelineWidth,
    }
  }

  const paletStart = new Date(getFechaInicioPalet(palet))
  const paletEnd = new Date(getFechaFinPalet(palet))
  const totalDuration = task.endDate.getTime() - task.startDate.getTime()
  const left = totalDuration > 0
    ? ((paletStart.getTime() - task.startDate.getTime()) / totalDuration) * 100
    : 0
  const width = totalDuration > 0
    ? ((paletEnd.getTime() - paletStart.getTime()) / totalDuration) * 100
    : 0

  return { left, width }
}

const getPaletOrdenValue = (palet, index) => {
  const numeroPalet = Number(palet?.numeroPalet)

  if (Number.isFinite(numeroPalet)) return numeroPalet

  const fechaInicio = new Date(getFechaInicioPalet(palet)).getTime()

  if (Number.isFinite(fechaInicio)) return fechaInicio

  return index
}

const getPaletsVisualesTimeline = (task) => {
  const palets = asArray(task?.palets)
  const paletsProducidos = Math.max(Number(task?.paletsProducidos ?? 0), 0)
  const paletsOrdenados = [...palets]
    .map((palet, index) => ({
      palet,
      orden: getPaletOrdenValue(palet, index),
      producidoExplicito: hasPaletProduccionExplicita(palet),
    }))
    .sort((paletA, paletB) => (
      Number(paletA.orden) - Number(paletB.orden)
      || String(paletA.palet?.idPlanificacionPalet ?? '').localeCompare(String(paletB.palet?.idPlanificacionPalet ?? ''))
    ))
    .map((paletInfo, index) => ({
      ...paletInfo,
      producido: paletInfo.producidoExplicito || index < paletsProducidos,
    }))
    .sort((paletA, paletB) => (
      Number(paletB.producido) - Number(paletA.producido)
      || Number(paletA.orden) - Number(paletB.orden)
    ))

  const minutosPalets = paletsOrdenados.map(({ palet }) => {
    const minutos = Number(getMinutosPrevistosPalet(palet))
    return Number.isFinite(minutos) && minutos > 0 ? minutos : 1
  })
  const totalMinutos = minutosPalets.reduce((acc, minutos) => acc + minutos, 0) || paletsOrdenados.length || 1
  let cursor = 0

  return paletsOrdenados.map(({ palet, producido }, index) => {
    const width = (minutosPalets[index] / totalMinutos) * 100
    const paletVisual = {
      ...palet,
      producidoTimeline: producido,
      timelineLeft: cursor,
      timelineWidth: width,
    }

    cursor += width

    return paletVisual
  })
}

const formatSignedMinutesSeconds = (minutes) => {
  const numericMinutes = Number(minutes)

  if (!Number.isFinite(numericMinutes)) return '-'

  const sign = numericMinutes > 0 ? '+' : numericMinutes < 0 ? '-' : ''
  const absoluteSeconds = Math.round(Math.abs(numericMinutes) * 60)
  const displayMinutes = Math.floor(absoluteSeconds / 60)
  const displaySeconds = absoluteSeconds % 60

  return `${sign}${displayMinutes}m ${String(displaySeconds).padStart(2, '0')}s`
}

const formatMinutesSeconds = (minutes) => {
  const numericMinutes = Number(minutes)

  if (!Number.isFinite(numericMinutes)) return '-'

  const absoluteSeconds = Math.round(Math.abs(numericMinutes) * 60)
  const displayMinutes = Math.floor(absoluteSeconds / 60)
  const displaySeconds = absoluteSeconds % 60

  return `${displayMinutes}m ${String(displaySeconds).padStart(2, '0')}s`
}

const formatDetailValue = (value, key) => {
  if (value === null || value === undefined || value === '') return '-'
  if (['minutosPorPalet', 'minutosTotales'].includes(key)) return formatMinutesSeconds(value)
  if (typeof value === 'boolean') return value ? 'Si' : 'No'
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(value)) return formatDateTime(value)
  if (typeof value === 'object') return JSON.stringify(value, null, 2)

  return String(value)
}

// eslint-disable-next-line no-unused-vars
const cabeceraPedidoFields = [
  ['numPedido', 'Nº Pedido'],
  ['codCliente', 'Nº Cliente'],
  ['desCliente', 'Cliente'],
  ['numDocExt', 'Nº Doc. Externo'],
  ['fechaCarga', 'Fecha Carga'],
  ['fechaEntrada', 'Fecha Entrada'],
  ['codTransportista', 'Nº Transportista'],
  ['desTransportista', 'Transportista'],
  ['matricula', 'Matricula'],
  ['matricula2', 'Matricula 2'],
  ['codDirEnvio', 'Nº Destino'],
  ['desDirEnvio', 'Destino'],
  ['sp', 'SP'],
  ['lote', 'Lote'],
  ['observacionesPedido', 'Observaciones pedido'],
  ['numRefPedido', 'Nº referencia pedido'],
  ['observacionesCarga', 'Observaciones carga'],
]

// eslint-disable-next-line no-unused-vars
const lineaPedidoFields = [
  ['numPedido', 'Nº Pedido'],
  ['numLinea', 'Nº Linea'],
  ['codAlmacen', 'Almacen'],
  ['codVariedad', 'Cod. variedad'],
  ['desVariedad', 'Variedad'],
  ['codGrupoVariedad', 'Grupo variedad'],
  ['codConfeccion', 'Cod. confeccion'],
  ['desConfeccion', 'Confeccion'],
  ['numLote', 'Lote'],
  ['codCalibre', 'Cod. calibre'],
  ['desCalibre', 'Calibre'],
  ['calibreAmpliado', 'Calibre ampliado'],
  ['codEnvase', 'Cod. envase'],
  ['desEnvase', 'Envase'],
  ['codPalet', 'Cod. palet'],
  ['desPalet', 'Palet'],
  ['codMarca', 'Cod. marca'],
  ['desMarca', 'Marca'],
  ['ggn', 'GGN'],
  ['codCategoria', 'Cod. categoria'],
  ['desCategoria', 'Categoria'],
  ['codOrigen', 'Cod. origen'],
  ['desOrigen', 'Origen'],
  ['observacion', 'Observacion'],
  ['cantidadPales', 'Cantidad palets'],
  ['bultosPalet', 'Bultos palet'],
  ['kilosBrutos', 'Kilos brutos'],
  ['kilosNetos', 'Kilos netos'],
  ['kilosOficiales', 'Kilos oficiales'],
  ['finalizado', 'Finalizado'],
  ['fechaFinalizacion', 'Fecha finalizacion'],
]

// eslint-disable-next-line no-unused-vars
const confeccionPedidoFields = [
  ['codConfeccion', 'Cod. confeccion'],
  ['desConfeccion', 'Confeccion'],
  ['codGrupoConfeccion', 'Grupo confeccion'],
  ['peso', 'Peso'],
  ['unidades', 'Unidades'],
]

// eslint-disable-next-line no-unused-vars
const planificacionPedidoFields = [
  ['idPlanificacion', 'Id planificacion'],
  ['codMaquina', 'Cod. maquina'],
  ['codAlmacen', 'Almacen'],
  ['cantidadPales', 'Cantidad palets'],
  ['fechaInicio', 'Fecha inicio'],
  ['fechaInicioPrevista', 'Inicio previsto'],
  ['fechaFinPrevista', 'Fin previsto'],
  ['fechaFinEstimada', 'Fin estimado'],
  ['estado', 'Estado'],
  ['minutosPorPalet', 'Minutos por palet'],
  ['minutosTotales', 'Minutos totales'],
]

// eslint-disable-next-line no-unused-vars
const produccionPedidoFields = [
  ['paletsPedido', 'Palets pedido'],
  ['paletsProducidos', 'Palets producidos'],
  ['paletsPendientes', 'Palets pendientes'],
  ['porcentajeProducido', '% producido'],
  ['finalizado', 'Finalizado'],
  ['fechaFinalizacion', 'Fecha finalizacion'],
]

// eslint-disable-next-line no-unused-vars
const parametroTiempoFields = [
  ['codGrupoConfeccion', 'Grupo confeccion'],
  ['codGrupoVariedad', 'Grupo variedad'],
  ['peso', 'Peso'],
  ['valor', 'Unidades por minuto'],
]

const detallePedidoFields = [
  ['numPedido', 'Num. pedido'],
  ['desCliente', 'Cliente'],
  ['fechaCarga', 'Fecha carga'],
  ['desDirEnvio', 'Destino'],
  ['sp', 'SP'],
  ['observacionesPedido', 'Observaciones pedido'],
  ['observacionesCarga', 'Observaciones carga'],
  ['desVariedad', 'Variedad'],
  ['desConfeccion', 'Confeccion'],
  ['numLote', 'Lote'],
  ['desCalibre', 'Calibre'],
  ['desEnvase', 'Envase'],
  ['desPalet', 'Palet'],
  ['desMarca', 'Marca'],
  ['ggn', 'GGN'],
  ['desCategoria', 'Categoria'],
  ['observacion', 'Observacion'],
  ['cantidadPales', 'Cantidad palets'],
  ['bultosPalet', 'Bultos palet'],
]

const getPedidoDetailRows = (data, fields) => (
  fields.map(([key, label]) => ({
    key,
    label,
    value: formatDetailValue(data?.[key], key),
  }))
)

const tieneDatosDetalle = (data, fields) => (
  Boolean(data) && fields.some(([key]) => {
    const value = data?.[key]
    return value !== null && value !== undefined && value !== ''
  })
)

const cambioFieldLabels = {
  codTransportista: 'Cod. transportista',
  desTransportista: 'Transportista',
  eliminadoBusinessCentral: 'Eliminado Business Central',
  fechaEliminadoBusinessCentral: 'Fecha eliminado Business Central',
  fechaCreacionBusinessCentral: 'Fecha creacion Business Central',
  fechaModificacionBusinessCentral: 'Fecha modificacion Business Central',
  matricula: 'Matricula',
  matricula2: 'Matricula 2',
  observacionesCarga: 'Observaciones carga',
  observacionesPedido: 'Observaciones pedido',
}

const getCambioLabel = (key) => (
  cambioFieldLabels[key]
  || String(key)
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (firstLetter) => firstLetter.toUpperCase())
)

const parseJsonIfNeeded = (value) => {
  if (typeof value !== 'string') return value

  const trimmedValue = value.trim()

  if (!trimmedValue.startsWith('{') && !trimmedValue.startsWith('[')) return value

  try {
    return JSON.parse(trimmedValue)
  } catch (error) {
    return value
  }
}

const hasCambioValues = (cambio) => {
  const parsedCambio = parseJsonIfNeeded(cambio)

  return Boolean(parsedCambio && typeof parsedCambio === 'object' && Object.keys(parsedCambio).length > 0)
}

const getCambioRowsDetalle = (cambio) => (
  Object.entries(parseJsonIfNeeded(cambio) || {}).map(([key, value]) => ({
    key,
    label: getCambioLabel(key),
    anterior: formatDetailValue(value?.anterior, key),
    nuevo: formatDetailValue(value?.nuevo, key),
  }))
)

const getSeccionCambio = (path) => {
  const pathText = path.join('.').toLowerCase()

  if (pathText.includes('cabecera')) return 'Cabecera'
  if (pathText.includes('linea')) return 'Linea'
  if (pathText.includes('planificacion')) return 'Planificacion'

  return 'Pedido'
}

const getCambiosDesdeObjeto = (source) => {
  const eventos = []
  const cambiosVistos = new Set()
  const objetosVisitados = new Set()

  const visitar = (value, path = [], parent = null) => {
    const parsedValue = parseJsonIfNeeded(value)

    if (!parsedValue || typeof parsedValue !== 'object') return
    if (objetosVisitados.has(parsedValue)) return

    objetosVisitados.add(parsedValue)

    Object.entries(parsedValue).forEach(([key, child]) => {
      const parsedChild = parseJsonIfNeeded(child)
      const keyLower = key.toLowerCase()

      if ((keyLower === 'cambio' || keyLower === 'cambios') && hasCambioValues(parsedChild) && !cambiosVistos.has(parsedChild)) {
        cambiosVistos.add(parsedChild)
        eventos.push({
          key: `cambio-${path.concat(key).join('-')}-${eventos.length}`,
          seccion: parent?.seccion || getSeccionCambio(path),
          tipoEvento: parent?.tipoEvento || 'ACTUALIZADO',
          fechaEvento: parent?.fechaEvento ? formatDateTime(parent.fechaEvento) : '-',
          rows: getCambioRowsDetalle(parsedChild),
        })
      }

      visitar(parsedChild, path.concat(key), parsedValue)
    })
  }

  visitar(source)

  return eventos.filter((evento) => evento.rows.length > 0)
}

const deduplicarCambiosDetalle = (eventos) => {
  const vistos = new Set()

  return eventos.filter((evento) => {
    const firma = JSON.stringify({
      seccion: evento.seccion,
      tipoEvento: evento.tipoEvento,
      fechaEvento: evento.fechaEvento,
      rows: evento.rows,
    })

    if (vistos.has(firma)) return false

    vistos.add(firma)
    return true
  })
}

const getCambiosFirma = (eventos) => {
  if (!Array.isArray(eventos) || eventos.length === 0) return ''

  return JSON.stringify(eventos.map((evento) => ({
    seccion: evento.seccion,
    tipoEvento: evento.tipoEvento,
    fechaEvento: evento.fechaEvento,
    rows: evento.rows,
  })))
}

const getCambiosPlanificacionKey = (task) => (
  task?.idPlanificacion
    ? `planificacion-${task.idPlanificacion}`
    : `pedido-${task?.numPedido ?? task?.code ?? ''}-${task?.numLinea ?? ''}`
)

const getCambiosVistosStorage = () => {
  try {
    return JSON.parse(localStorage.getItem(CAMBIOS_VISTOS_STORAGE_KEY) || '{}')
  } catch (error) {
    return {}
  }
}

const getCambioVistoFirma = (key) => getCambiosVistosStorage()[key]

const setCambioVistoFirma = (key, firma) => {
  if (!key || !firma) return

  try {
    localStorage.setItem(CAMBIOS_VISTOS_STORAGE_KEY, JSON.stringify({
      ...getCambiosVistosStorage(),
      [key]: firma,
    }))
  } catch (error) {
    console.error('No se pudo guardar la lectura de cambios:', error)
  }
}

const getPaletDisplayInfo = (palet) => {
  const fechaInicio = getFechaInicioPalet(palet)
  const finPrevisto = getFechaFinPrevistaCalculadaPalet(palet)
  const finReal = getFechaFinRealPalet(palet)
  const minutos = getMinutosPrevistosPalet(palet) ?? '-'
  const diferencia = getDiferenciaFinPalet(palet)

  return {
    previsto: `${formatTime(fechaInicio)}-${formatTime(finPrevisto)}`,
    real: `${formatTime(fechaInicio)}-${formatTime(finReal)}`,
    minutos: minutos === '-' ? '-' : `${Number(minutos).toFixed(0)}m`,
    diferencia,
    diferenciaLabel: formatSignedMinutesSeconds(diferencia),
  }
}

const getDiferenciaFinPalet = (palet) => {
  const fechaInicio = getFechaInicioPalet(palet)
  const finReal = getFechaFinRealPalet(palet)
  const minutosPrevistos = getMinutosPrevistosPalet(palet)
  const minutosReales = getMinutesBetween(fechaInicio, finReal)

  if (!Number.isFinite(Number(minutosPrevistos)) || !Number.isFinite(Number(minutosReales))) return '-'

  return Number(minutosReales) - Number(minutosPrevistos)
}

const getFechaInicioPlanificacion = (planificacion) => (
  planificacion?.fechaInicioPrevista || planificacion?.fechaInicio || planificacion?.fechaInicioTeorica
)

const getFechaFinPlanificacion = (planificacion) => (
  planificacion?.fechaFinPrevista || planificacion?.fechaFinEstimada || planificacion?.fechaFin
)

const getFechaInicioPalet = (palet) => (
  palet?.fechaInicioPrevista || palet?.fechaInicio || palet?.fechaInicioTeorica
)

const getFechaFinPalet = (palet) => (
  palet?.fechaFinPrevista || palet?.fechaFin || palet?.fechaFinTeorica
)

const normalizarMaquinasTablero = (response) => {
  const tablero = normalizarArray(response, 'tablero')
  const maquinas = tablero.length ? tablero : normalizarArray(response, 'maquinas')
  const planificaciones = maquinas.length ? [] : normalizarArray(response, 'planificaciones')

  if (maquinas.length) return maquinas

  const maquinasMap = new Map()

  planificaciones.forEach((planificacion) => {
    const codMaquina = planificacion.codMaquina
    const maquinaActual = maquinasMap.get(String(codMaquina)) || {
      codMaquina,
      desMaquina: planificacion.desMaquina || `Maquina ${codMaquina}`,
      planificaciones: [],
    }

    maquinaActual.planificaciones.push(planificacion)
    maquinasMap.set(String(codMaquina), maquinaActual)
  })

  return Array.from(maquinasMap.values())
}

const getDropDate = (event, timelineElement, timelineWindow, timelineWidth, visibleHours) => {
  const rect = timelineElement.getBoundingClientRect()
  const x = clamp(event.clientX - rect.left, 0, timelineWidth)
  const snapWidth = (HOUR_CELL_WIDTH / 60) * DROP_SNAP_MINUTES
  const maxSnapIndex = (visibleHours * 60 / DROP_SNAP_MINUTES) - 1
  const snapIndex = Math.min(Math.floor(x / snapWidth), maxSnapIndex)

  return new Date(timelineWindow.start.getTime() + (snapIndex * DROP_SNAP_MINUTES * 60 * 1000))
}

const setDragPayload = (event, payload) => {
  event.dataTransfer.effectAllowed = 'move'
  event.dataTransfer.setData(DRAG_DATA_TYPE, JSON.stringify(payload))
}

const getDragPayload = (event) => {
  const rawPayload = event.dataTransfer.getData(DRAG_DATA_TYPE)

  if (!rawPayload) return null

  try {
    return JSON.parse(rawPayload)
  } catch (error) {
    console.error('Payload de arrastre no valido:', error)
    return null
  }
}

const getApiErrorInfo = (error) => {
  const data = error?.response?.data
  const message = Array.isArray(data?.message)
    ? data.message.join('\n')
    : (data?.message || error?.message || 'Error al guardar la planificacion')

  return {
    message,
    filtro: data?.filtro,
  }
}

const getPlanificacionStatus = (planificacion) => {
  if (isEliminadoBusinessCentral(planificacion)) return 'cancelled'

  const estado = String(planificacion.estado ?? '').toUpperCase()
  const fechaInicio = new Date(getFechaInicioPlanificacion(planificacion))
  const fechaFin = new Date(getFechaFinPlanificacion(planificacion))
  const fechaFinTeorica = planificacion.fechaFinTeorica ? new Date(planificacion.fechaFinTeorica) : fechaFin
  const ahora = new Date()
  const retrasoMinutos = Number(planificacion.retrasoMinutos ?? 0)
  const algunPaletRetrasado = Array.isArray(planificacion.palets)
    && planificacion.palets.some((palet) => Number(palet.retrasoMinutos ?? 0) > 0)

  if (getProduccionPlanificacion(planificacion).finalizado || planificacion.produccion?.finalizado) return 'completed'
  if (fechaInicio.getTime() > ahora.getTime()) return 'waiting'
  if (['RETRASADO'].includes(estado)) return 'delayed'
  if (retrasoMinutos > 0 || algunPaletRetrasado) return 'delayed'
  if (fechaFinTeorica.getTime() < ahora.getTime()) return 'delayed'
  if (['PENDIENTE', 'EN_ESPERA'].includes(estado)) return 'waiting'
  if (
    ['EN_CURSO', 'ACTIVO'].includes(estado)
    || (fechaInicio.getTime() <= ahora.getTime() && fechaFin.getTime() >= ahora.getTime())
  ) return 'active'

  return 'waiting'
}

const getPlanificacionTasks = (planificaciones, pedidosPendientes) => {
  const pedidosPorLinea = new Map(pedidosPendientes.map((pedido) => [getPedidoKey(pedido), pedido]))
  const pedidosPorNumero = new Map()

  pedidosPendientes.forEach((pedido) => {
    const numPedido = pedido?.numPedido || pedido?.pedido?.cabecera?.numPedido || pedido?.pedido?.linea?.numPedido

    if (numPedido && !pedidosPorNumero.has(String(numPedido))) {
      pedidosPorNumero.set(String(numPedido), pedido)
    }
  })

  return planificaciones
    .filter((planificacion) => getFechaInicioPlanificacion(planificacion) && getFechaFinPlanificacion(planificacion))
    .map((planificacion) => {
      const pedidoLineaPendiente = pedidosPorLinea.get(getPedidoKey(planificacion))
      const pedidoReferencia = pedidoLineaPendiente || pedidosPorNumero.get(String(planificacion.numPedido)) || {}
      const lineaPendiente = pedidoLineaPendiente?.pedido?.linea || pedidoLineaPendiente?.linea || pedidoLineaPendiente || {}
      const cabeceraPendiente = pedidoReferencia?.pedido?.cabecera || pedidoReferencia?.cabecera || pedidoReferencia || {}
      const confeccionPendiente = pedidoReferencia?.pedido?.confeccion || pedidoReferencia?.confeccion || {}
      const lineaPedido = planificacion.pedido?.linea || lineaPendiente
      const cabeceraPedido = planificacion.pedido?.cabecera || cabeceraPendiente
      const confeccionPedido = planificacion.pedido?.confeccion || confeccionPendiente
      const desVariedad = lineaPedido.desVariedad || planificacion.desVariedad || '-'
      const desConfeccion = confeccionPedido.desConfeccion || lineaPedido.desConfeccion || planificacion.desConfeccion || '-'
      const desCliente = cabeceraPedido.desCliente || lineaPedido.desCliente || planificacion.desCliente || '-'
      const desDirEnvio = cabeceraPedido.desDirEnvio || lineaPedido.desDirEnvio || planificacion.desDirEnvio || '-'
      const desMarca = lineaPedido.desMarca || planificacion.desMarca || '-'
      const cantidadPales = getCantidadPales(planificacion) || getCantidadPales(pedidoReferencia)
      const produccion = planificacion.produccion || {}
      const produccionPlanificacion = getProduccionPlanificacion(planificacion)
      const porcentaje = produccionPlanificacion.porcentajeProducido
      const paletsProducidos = produccionPlanificacion.paletsProducidos
      const startDate = new Date(getFechaInicioPlanificacion(planificacion))
      const endDate = new Date(getFechaFinPlanificacion(planificacion))
      const palets = getPaletsConInicioRealEncadenado(
        Array.isArray(planificacion.palets) ? planificacion.palets : [],
        getFechaInicioPlanificacion(planificacion)
      )

      return {
        key: `${planificacion.idPlanificacion ?? getPedidoKey(planificacion)}`,
        idPlanificacion: planificacion.idPlanificacion,
        codMaquina: planificacion.codMaquina,
        numPedido: planificacion.numPedido,
        numLinea: planificacion.numLinea,
        code: planificacion.numPedido,
        title: desVariedad || planificacion.numPedido,
        desVariedad,
        codVariedad: lineaPedido.codVariedad || planificacion.codVariedad || pedidoReferencia.codVariedad,
        desConfeccion,
        desCliente,
        desDirEnvio,
        desMarca,
        confeccion: desConfeccion,
        calibre: lineaPedido.desCalibre || '-',
        cliente: desCliente,
        cantidadPales,
        paletsProducidos,
        paletsProducidosPedido: getPaletsProducidos(planificacion),
        detail: `${paletsProducidos} / ${cantidadPales} palets`,
        note: [lineaPedido.desCalibre ? `Calibre ${lineaPedido.desCalibre}` : null, cabeceraPedido.desCliente || lineaPedido.desCliente].filter(Boolean).join(' | '),
        startDate,
        endDate,
        endLabel: dateTimeFormatter.format(endDate),
        palets,
        pedidoRaw: pedidoReferencia,
        planificacionRaw: planificacion,
        cabeceraPedido,
        lineaPedido,
        confeccionPedido,
        produccion,
        produccionPlanificacion,
        parametroTiempo: planificacion.parametroTiempo,
        eliminadoBusinessCentral: isEliminadoBusinessCentral(planificacion),
        status: getPlanificacionStatus(planificacion),
        progress: Number.isFinite(porcentaje) ? clamp(porcentaje, 0, 100) : 0,
        badge: `${Math.round(Number.isFinite(porcentaje) ? porcentaje : 0)}%`,
      }
    })
}

const getPlanificacionesMaquina = (maquina) => (
  maquina?.planificaciones
  || maquina?.items
  || maquina?.tareas
  || maquina?.lineas
  || []
)

const getLinesFromTablero = (
  maquinasTablero,
  salidasProduccion,
  pedidosPendientes,
  tiposMaquinas = [],
  coloresUsuarioMaquinas = []
) => {
  const salidasPorMaquina = new Map(salidasProduccion.map((salida) => [String(salida.codMaquina), salida]))
  const tiposPorCodigo = new Map(tiposMaquinas.map((tipoMaquina) => [String(tipoMaquina.codTipoMaquina), tipoMaquina]))
  const coloresUsuarioPorMaquina = new Map(
    coloresUsuarioMaquinas
      .map((colorMaquinaUsuario) => [
        normalizarClave(getCodMaquinaColorUsuario(colorMaquinaUsuario)),
        getColorMaquinaUsuario(colorMaquinaUsuario),
      ])
      .filter(([codMaquina, colorMaquina]) => codMaquina && colorMaquina)
  )
  const maquinasBase = maquinasTablero.length
    ? maquinasTablero
    : salidasProduccion.map((salida) => ({ ...salida, planificaciones: [] }))

  return (
    maquinasBase
      .filter((maquina) => maquina.activa !== false)
      .sort((maquinaA, maquinaB) => (
        Number(maquinaA.ordenVisual ?? salidasPorMaquina.get(String(maquinaA.codMaquina))?.ordenVisual ?? 0)
        - Number(maquinaB.ordenVisual ?? salidasPorMaquina.get(String(maquinaB.codMaquina))?.ordenVisual ?? 0)
        || Number(maquinaA.codMaquina ?? 0) - Number(maquinaB.codMaquina ?? 0)
      ))
      .map((maquina) => {
        const salida = salidasPorMaquina.get(String(maquina.codMaquina)) || {}
        const tipoMaquina = tiposPorCodigo.get(String(
          maquina.tipoMaquina
          ?? maquina.codTipoMaquina
          ?? salida.tipoMaquina
          ?? salida.codTipoMaquina
        )) || {}
        const planificacionesMaquina = getPlanificacionesMaquina(maquina)
          .slice()
          .sort((planificacionA, planificacionB) => (
            new Date(getFechaInicioPlanificacion(planificacionA)).getTime()
            - new Date(getFechaInicioPlanificacion(planificacionB)).getTime()
          ))
        const colorUsuario = coloresUsuarioPorMaquina.get(normalizarClave(maquina.codMaquina))
        const colorGlobal = (
          maquina.colorMaquina
          || maquina.tipos_maquinas?.colorMaquina
          || salida.colorMaquina
          || salida.tipos_maquinas?.colorMaquina
          || tipoMaquina.colorMaquina
          || ''
        )

        return {
          codMaquina: maquina.codMaquina,
          name: maquina.desMaquina || maquina.nombreMaquina || salida.desMaquina || `Maquina ${maquina.codMaquina}`,
          area: '',
          utilization: maquina.activa === false || salida.activa === false ? 'Inactiva' : 'Activa',
          colorMaquina: colorUsuario || colorGlobal,
          colorMaquinaUsuario: colorUsuario || '',
          colorMaquinaGlobal: colorGlobal,
          raw: maquina,
          salidaRaw: salida,
          parametrosTiempo: getParametrosTiempoMaquina(maquina, salida),
          tasks: getPlanificacionTasks(planificacionesMaquina, pedidosPendientes),
        }
      })
  )
}

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const toTimelinePercent = (date, windowStart, totalHours) => (
  ((date.getTime() - windowStart.getTime()) / (totalHours * MS_PER_HOUR)) * 100
)

const getTimelineWindow = (dateValue, visibleHours) => {
  const start = new Date(`${dateValue || toDateInputValue()}T12:00:00`)

  start.setHours(5, 0, 0, 0)

  return {
    start,
    end: new Date(start.getTime() + (visibleHours * MS_PER_HOUR)),
  }
}

const getDayLabel = (date, centerDate) => {
  const dateDay = new Date(date)
  const centerDay = new Date(centerDate)

  dateDay.setHours(0, 0, 0, 0)
  centerDay.setHours(0, 0, 0, 0)

  const dayDiff = Math.round((dateDay.getTime() - centerDay.getTime()) / (24 * MS_PER_HOUR))

  if (dayDiff === -1) return 'Ayer'
  if (dayDiff === 0) return 'Hoy'
  if (dayDiff === 1) return 'Mañana'

  return dayFormatter.format(date)
}

const getHourMarks = (windowStart, visibleHours) => (
  Array.from({ length: (visibleHours * 4) + 1 }, (_, index) => {
    const date = new Date(windowStart.getTime() + (index * 0.25 * MS_PER_HOUR))

    return {
      isHour: date.getMinutes() === 0,
      day: getDayLabel(date, windowStart),
      time: hourFormatter.format(date),
    }
  })
)

const getDaySegments = (windowStart, visibleHours) => {
  const windowEnd = new Date(windowStart.getTime() + (visibleHours * MS_PER_HOUR))
  const segments = []
  let cursor = new Date(windowStart)

  while (cursor.getTime() < windowEnd.getTime()) {
    const nextDay = new Date(cursor)

    nextDay.setHours(24, 0, 0, 0)

    const segmentEnd = new Date(Math.min(nextDay.getTime(), windowEnd.getTime()))
    const startOffset = (cursor.getTime() - windowStart.getTime()) / (visibleHours * MS_PER_HOUR)
    const endOffset = (segmentEnd.getTime() - windowStart.getTime()) / (visibleHours * MS_PER_HOUR)

    segments.push({
      label: getDayLabel(cursor, windowStart),
      left: `${startOffset * 100}%`,
      width: `${Math.max((endOffset - startOffset) * 100, 0)}%`,
    })

    cursor = segmentEnd
  }

  return segments
}

const getTaskPosition = (start, end, windowStart, windowEnd) => {
  const taskStart = start instanceof Date
    ? start
    : new Date(windowStart.getTime() + (Number(start) * MS_PER_HOUR))
  const taskEnd = end instanceof Date
    ? end
    : new Date(windowStart.getTime() + (Number(end) * MS_PER_HOUR))

  if (Number.isNaN(taskStart.getTime()) || Number.isNaN(taskEnd.getTime())) return null

  const windowStartTime = windowStart.getTime()
  const windowEndTime = windowEnd.getTime()
  const clampedStart = clamp(taskStart.getTime(), windowStartTime, windowEndTime)
  const clampedEnd = clamp(taskEnd.getTime(), windowStartTime, windowEndTime)

  if (clampedEnd <= clampedStart) return null

  const left = ((clampedStart - windowStartTime) / MS_PER_HOUR) * HOUR_CELL_WIDTH
  const width = Math.max(((clampedEnd - clampedStart) / MS_PER_HOUR) * HOUR_CELL_WIDTH, MIN_TASK_CARD_WIDTH)

  return {
    left: `${left}px`,
    width: `${width}px`,
  }
}

const getTimelineGridBackground = (backgroundColor = '#FFFFFF') => `
  repeating-linear-gradient(
    to right,
    rgba(148, 163, 184, 0.16) 0px,
    rgba(148, 163, 184, 0.16) 1px,
    transparent 1px,
    transparent ${QUARTER_HOUR_CELL_WIDTH}px
  ),
  linear-gradient(180deg, ${backgroundColor} 0%, ${backgroundColor} 100%)
`

export const MainPage = () => {
  const timelineScrollRef = React.useRef(null)
  const dragScrollState = React.useRef({
    isDragging: false,
    pointerId: null,
    startX: 0,
    scrollLeft: 0,
  })
  const touchPedidoDragState = React.useRef({
    isPointerDown: false,
    isDragging: false,
    pointerId: null,
    startX: 0,
    startY: 0,
    payload: null,
  })
  const [now, setNow] = React.useState(() => new Date())
  const [visibleHours, setVisibleHours] = React.useState(DEFAULT_VISIBLE_HOURS)
  const [isTimelineDragging, setIsTimelineDragging] = React.useState(false)
  const [salidasProduccion, setSalidasProduccion] = React.useState([])
  const [tiposMaquinas, setTiposMaquinas] = React.useState([])
  const [coloresUsuarioMaquinas, setColoresUsuarioMaquinas] = React.useState([])
  const [coloresUsuarioVariedades, setColoresUsuarioVariedades] = React.useState([])
  const coloresPorVariedad = React.useMemo(() => Object.fromEntries(
    coloresUsuarioVariedades.map(v => [v.codVariedad, v.colorVariedad])
  ), [coloresUsuarioVariedades])
  const [gruposConfeccion, setGruposConfeccion] = React.useState([])
  const [parametrosTiempo, setParametrosTiempo] = React.useState([])
  const [grupoConfeccionFiltro, setGrupoConfeccionFiltro] = React.useState([])
  const [loadingSalidas, setLoadingSalidas] = React.useState(true)
  const [maquinasTablero, setMaquinasTablero] = React.useState([])
  const [loadingPlanificaciones, setLoadingPlanificaciones] = React.useState(true)
  const [pedidosPendientes, setPedidosPendientes] = React.useState([])
  const [loadingPedidos, setLoadingPedidos] = React.useState(true)
  const [guardandoPlanificacion, setGuardandoPlanificacion] = React.useState(false)
  const [splitDialog, setSplitDialog] = React.useState(null)
  const [detallePedido, setDetallePedido] = React.useState(null)
  const [, setCambiosVistosVersion] = React.useState(0)
  const [dropPreview, setDropPreview] = React.useState(null)
  const [touchPedidoDragPreview, setTouchPedidoDragPreview] = React.useState(null)
  const [pedidosContraidos, setPedidosContraidos] = React.useState(true)
  const [vistaCompacta, setVistaCompacta] = React.useState(false)
  const [errorPlanificacion, setErrorPlanificacion] = React.useState(null)
  const [codAlmacenSeleccionado, setCodAlmacenSeleccionado] = React.useState(() => getAlmacenSeleccionado())
  const [diaPlanificador, setDiaPlanificador] = React.useState(() => toDateInputValue())
  const [puedeModificarPlanificacion, setPuedeModificarPlanificacion] = React.useState(false)
  const timelineWindow = React.useMemo(() => getTimelineWindow(diaPlanificador, visibleHours), [diaPlanificador, visibleHours])
  const hourMarks = React.useMemo(() => getHourMarks(timelineWindow.start, visibleHours), [timelineWindow.start, visibleHours])
  const daySegments = React.useMemo(() => getDaySegments(timelineWindow.start, visibleHours), [timelineWindow.start, visibleHours])
  const totalHours = visibleHours
  const timelineWidth = visibleHours * HOUR_CELL_WIDTH
  const rowHeight = vistaCompacta ? COMPACT_ROW_HEIGHT : ROW_HEIGHT
  const taskCardHeight = vistaCompacta ? COMPACT_TASK_CARD_HEIGHT : TASK_CARD_HEIGHT
  const taskCardTop = vistaCompacta ? COMPACT_TASK_CARD_TOP : TASK_CARD_TOP
  const labelWidth = vistaCompacta ? 150 : LABEL_WIDTH
  const currentTimeRawPercent = toTimelinePercent(now, timelineWindow.start, totalHours)
  const currentTimeVisible = currentTimeRawPercent >= 0 && currentTimeRawPercent <= 100
  const currentTimePercent = clamp(currentTimeRawPercent, 0, 100)
  const timelineGridBackground = React.useMemo(() => getTimelineGridBackground(), [])
  const gruposConfeccionFiltroNormalizados = React.useMemo(() => (
    normalizarFiltroGruposConfeccion(grupoConfeccionFiltro)
  ), [grupoConfeccionFiltro])
  const pedidosPendientesFiltradosGrupo = React.useMemo(() => {
    if (!gruposConfeccionFiltroNormalizados.length) return pedidosPendientes

    return pedidosPendientes.filter((pedido) => (
      gruposConfeccionFiltroNormalizados.includes(normalizarClave(getCodGrupoConfeccionPedido(pedido)))
    ))
  }, [gruposConfeccionFiltroNormalizados, pedidosPendientes])
  const parametrosTiempoPorMaquina = React.useMemo(() => (
    parametrosTiempo.reduce((acc, parametro) => {
      const codMaquina = normalizarClave(parametro?.codMaquina)

      if (!codMaquina) return acc
      if (!acc.has(codMaquina)) acc.set(codMaquina, [])

      acc.get(codMaquina).push(parametro)
      return acc
    }, new Map())
  ), [parametrosTiempo])
  const maquinaCompatibleConGrupo = React.useCallback((line) => {
    if (!gruposConfeccionFiltroNormalizados.length) return true

    const codMaquina = normalizarClave(line?.codMaquina)
    const parametrosMaquina = [
      ...(parametrosTiempoPorMaquina.get(codMaquina) || []),
      ...asArray(line?.parametrosTiempo),
    ]

    if (!parametrosMaquina.length) return false

    const pedidosGrupo = pedidosPendientesFiltradosGrupo.length
      ? pedidosPendientesFiltradosGrupo
      : gruposConfeccionFiltroNormalizados.map((codGrupoConfeccion) => ({ codGrupoConfeccion }))

    const coincideConPedido = parametrosMaquina.some((parametro) => (
      pedidosGrupo.some((pedido) => parametroTiempoCoincideConPedido(
        parametro,
        pedido,
        getCodGrupoConfeccionPedido(pedido)
      ))
    ))

    if (coincideConPedido) return true

    return parametrosMaquina.some((parametro) => (
      gruposConfeccionFiltroNormalizados.includes(normalizarClave(parametro?.codGrupoConfeccion))
    ))
  }, [gruposConfeccionFiltroNormalizados, parametrosTiempoPorMaquina, pedidosPendientesFiltradosGrupo])
  const productionLines = React.useMemo(() => {
    const lines = getLinesFromTablero(
      maquinasTablero,
      salidasProduccion,
      pedidosPendientes,
      tiposMaquinas,
      coloresUsuarioMaquinas
    )

    if (!gruposConfeccionFiltroNormalizados.length) return lines

    return lines.filter((line) => maquinaCompatibleConGrupo(line))
  }, [coloresUsuarioMaquinas, gruposConfeccionFiltroNormalizados, maquinaCompatibleConGrupo, maquinasTablero, pedidosPendientes, salidasProduccion, tiposMaquinas])
  const pedidosPendientesPanel = React.useMemo(() => {
    const pedidosPorClave = new Map()
    const planificacionesPorPedido = new Map()
    const pedidosCompletados = new Set()

    pedidosPendientesFiltradosGrupo.forEach((pedido) => {
      const totalPedido = getCantidadPales(pedido)
      const paletsProducidos = getPaletsProducidos(pedido)
      if (totalPedido > 0 && paletsProducidos >= totalPedido) {
        pedidosCompletados.add(getPedidoKey(pedido))
        return
      }

      pedidosPorClave.set(getPedidoKey(pedido), {
        ...pedido,
        cantidadPendiente: Math.min(
          getPaletsPendientesPlanificar(pedido),
          Math.max(totalPedido - paletsProducidos, 0),
        ),
      })
    })

    productionLines.forEach((line) => {
      line.tasks.forEach((task) => {
        const pedidoPlanificado = getPedidoPendienteDesdeTask(task)
        const pedidoKey = getPedidoKey(pedidoPlanificado)
        const resumenActual = planificacionesPorPedido.get(pedidoKey) || {
          cantidadPlanificada: 0,
          tasks: [],
          pedidoPlanificado,
        }

        resumenActual.cantidadPlanificada += Number(task.cantidadPales ?? 0)
        resumenActual.tasks.push(task)
        resumenActual.pedidoPlanificado = {
          ...resumenActual.pedidoPlanificado,
          ...pedidoPlanificado,
        }
        planificacionesPorPedido.set(pedidoKey, resumenActual)
      })
    })

    planificacionesPorPedido.forEach((resumen, pedidoKey) => {
      if (pedidosCompletados.has(pedidoKey)) return

      const pedidoPlanificado = resumen.pedidoPlanificado
      const codGrupoPedido = normalizarClave(getCodGrupoConfeccionPedido(pedidoPlanificado))

      if (
        gruposConfeccionFiltroNormalizados.length
        && !gruposConfeccionFiltroNormalizados.includes(codGrupoPedido)
      ) {
        return
      }

      const pedidoExistente = pedidosPorClave.get(pedidoKey)
      const totalPedido = Math.max(
        getCantidadPales(pedidoExistente),
        getCantidadPales(pedidoPlanificado),
        resumen.cantidadPlanificada,
      )
      // Produced pallets are part of the assigned quantity, not an additional allocation.
      const producidosTablero = resumen.tasks.reduce((total, task) => total + getPaletsProducidos(task), 0)
      // Every planning carries the same order-wide total; take its maximum, never its sum.
      const paletsProducidos = Math.max(
        getPaletsProducidos(pedidoExistente),
        producidosTablero,
        ...resumen.tasks.map((task) => Number(task.paletsProducidosPedido ?? 0)),
      )
      const pendientesAsignados = resumen.tasks.reduce((total, task) => (
        total + Math.max(getCantidadPales(task) - getPaletsProducidos(task), 0)
      ), 0)
      const pendienteBackend = Number(pedidoExistente?.cantidadPendiente ?? pedidoExistente?.paletsPendientesPlanificar ?? Number.NaN)
      const pendienteCalculado = Math.max(totalPedido - paletsProducidos - pendientesAsignados, 0)
      const cantidadPendiente = Number.isFinite(pendienteBackend)
        ? Math.max(Math.min(pendienteBackend, pendienteCalculado), 0)
        : pendienteCalculado
      const plannerStatus = getStatusPedidoPlanificado(resumen.tasks, totalPedido, paletsProducidos)

      if (cantidadPendiente <= 0 && plannerStatus === 'completed') {
        pedidosPorClave.delete(pedidoKey)
        return
      }

      pedidosPorClave.set(pedidoKey, {
        ...(pedidoExistente || pedidoPlanificado),
        cantidadPales: totalPedido,
        cantidadPlanificada: resumen.cantidadPlanificada,
        cantidadPendiente,
        paletsProducidos,
        __estaPlanificado: true,
        __plannerStatus: plannerStatus,
        __planificacionTask: resumen.tasks[0],
      })
    })

    return Array.from(pedidosPorClave.values())
  }, [gruposConfeccionFiltroNormalizados, pedidosPendientesFiltradosGrupo, productionLines])
  const summaryCardsPlanificador = React.useMemo(() => ([
    {
      label: 'Hora actual',
      value: hourFormatter.format(now),
      color: '#F57C00',
      icon: AccessTimeRoundedIcon,
    },
    {
      label: 'Palets por hora',
      value: 'No implementado',
      color: '#0F766E',
      icon: SpeedRoundedIcon,
    },
    {
      label: '% Producción Pedidos',
      value: 'No implementado',
      color: '#B45309',
      icon: Inventory2RoundedIcon,
    },
    {
      label: 'Fin ultimo pedido',
      value: 'No implementado',
      color: '#2563EB',
      icon: TaskAltRoundedIcon,
    },
    {
      label: 'Hora estimada fin produccion',
      value: 'No implementado',
      color: '#B91C1C',
      icon: HourglassBottomRoundedIcon,
    },
  ]), [now])

  const cargarPedidosPendientes = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingPedidos(true)

    try {
      const response = await getPedidosLineasPendientes(
        codAlmacenSeleccionado ? { codAlmacen: codAlmacenSeleccionado } : undefined
      )
      const pedidosData = normalizarArray(response, 'pedidos')
      const pedidosFiltrados = codAlmacenSeleccionado
        ? pedidosData.filter((pedido) => String(pedido.codAlmacen) === String(codAlmacenSeleccionado))
        : pedidosData

      setPedidosPendientes(pedidosFiltrados)
    } catch (error) {
      console.error('Error al cargar pedidos pendientes del planificador:', error)
      setPedidosPendientes([])
    } finally {
      if (!silent) setLoadingPedidos(false)
    }
  }, [codAlmacenSeleccionado])

  const cargarPlanificaciones = React.useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingPlanificaciones(true)

    try {
      const params = {
        ...(codAlmacenSeleccionado ? { codAlmacen: codAlmacenSeleccionado } : {}),
        ...(diaPlanificador ? { dia: diaPlanificador } : {}),
      }
      const response = await getPlanificacionesTablero(params)
      const maquinasData = normalizarMaquinasTablero(response)
      const maquinasFiltradas = codAlmacenSeleccionado
        ? maquinasData.filter((maquina) => (
          !maquina.codAlmacen || String(maquina.codAlmacen) === String(codAlmacenSeleccionado)
        ))
        : maquinasData

      setMaquinasTablero(maquinasFiltradas)
    } catch (error) {
      console.error('Error al cargar tablero de planificaciones:', error)
      setMaquinasTablero([])
    } finally {
      if (!silent) setLoadingPlanificaciones(false)
    }
  }, [codAlmacenSeleccionado, diaPlanificador])

  const refrescarPlanificador = React.useCallback(async ({ silent = false } = {}) => {
    await Promise.all([
      cargarPedidosPendientes({ silent }),
      cargarPlanificaciones({ silent }),
    ])
  }, [cargarPedidosPendientes, cargarPlanificaciones])

  React.useEffect(() => {
    const cargarPermisos = async () => {
      try {
        const session = await getSession()
        setPuedeModificarPlanificacion(puedeEditarPlanificacion(getUsuarioFromSession(session)))
      } catch (error) {
        console.error('Error al cargar permisos del planificador:', error)
        setPuedeModificarPlanificacion(false)
      }
    }

    cargarPermisos()
  }, [])

  React.useEffect(() => {
    const cargarDatosFiltroGrupo = async () => {
      try {
        const [gruposResponse, primeraPaginaParametros, tiposMaquinasResponse, coloresUsuarioResponse, coloresVariedadesResponse] = await Promise.all([
          getGruposConfeccion(),
          getParametrosTiempo({ page: 1, limit: 5000 }),
          getTiposMaquinas(),
          getMisColoresMaquinas().catch((error) => {
            console.error('Error al cargar colores personales de maquinas:', error)
            return []
          }),
          getMisColoresVariedades().catch((error) => {
            console.error('Error al cargar colores personales de variedades:', error)
            return []
          }),
        ])
        const parametrosPrimeraPagina = normalizarArray(primeraPaginaParametros, 'parametrosTiempo')
        const paginacionParametros = normalizarPaginacion(primeraPaginaParametros, parametrosPrimeraPagina.length)
        const paginasRestantes = paginacionParametros.totalPages > 1
          ? await Promise.all(
            Array.from({ length: paginacionParametros.totalPages - 1 }, (_, index) => (
              getParametrosTiempo({ page: index + 2, limit: paginacionParametros.limit })
            ))
          )
          : []
        const parametrosCompletos = paginasRestantes.reduce((acc, response) => (
          acc.concat(normalizarArray(response, 'parametrosTiempo'))
        ), parametrosPrimeraPagina)

        setGruposConfeccion(normalizarArray(gruposResponse, 'gruposConfeccion'))
        setParametrosTiempo(parametrosCompletos)
        setTiposMaquinas(normalizarArray(tiposMaquinasResponse, 'tiposMaquinas'))
        setColoresUsuarioMaquinas(normalizarArray(coloresUsuarioResponse, 'coloresMaquinas'))
        setColoresUsuarioVariedades(normalizarArray(coloresVariedadesResponse, 'coloresVariedades'))
      } catch (error) {
        console.error('Error al cargar datos del filtro de grupo de confeccion:', error)
        setGruposConfeccion([])
        setParametrosTiempo([])
        setTiposMaquinas([])
        setColoresUsuarioMaquinas([])
        setColoresUsuarioVariedades([])
      }
    }

    cargarDatosFiltroGrupo()
  }, [])

  React.useEffect(() => {
    const handleAlmacenChange = (event) => {
      setCodAlmacenSeleccionado(event.detail || getAlmacenSeleccionado())
    }

    const handleStorageChange = (event) => {
      if (event.key === ALMACEN_SELECCIONADO_STORAGE_KEY) {
        setCodAlmacenSeleccionado(event.newValue || '')
      }
    }

    window.addEventListener(ALMACEN_SELECCIONADO_EVENT, handleAlmacenChange)
    window.addEventListener('storage', handleStorageChange)

    return () => {
      window.removeEventListener(ALMACEN_SELECCIONADO_EVENT, handleAlmacenChange)
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [])

  React.useEffect(() => {
    let refreshInProgress = false

    const actualizarVistaViva = async () => {
      if (refreshInProgress) return

      refreshInProgress = true

      try {
        await refrescarPlanificador({ silent: true })
      } finally {
        setNow(new Date())
        refreshInProgress = false
      }
    }

    const intervalId = window.setInterval(actualizarVistaViva, 30000)

    return () => window.clearInterval(intervalId)
  }, [refrescarPlanificador])

  React.useEffect(() => {
    const cargarSalidasProduccion = async () => {
      setLoadingSalidas(true)

      try {
        const response = await getSalidasProduccion(
          codAlmacenSeleccionado ? { codAlmacen: codAlmacenSeleccionado } : undefined
        )
        const salidasData = normalizarArray(response, 'salidasProduccion')
        const salidasFiltradas = codAlmacenSeleccionado
          ? salidasData.filter((salida) => String(salida.codAlmacen) === String(codAlmacenSeleccionado))
          : salidasData

        setSalidasProduccion(salidasFiltradas)
      } catch (error) {
        console.error('Error al cargar salidas de produccion para el planificador:', error)
        setSalidasProduccion([])
      } finally {
        setLoadingSalidas(false)
      }
    }

    cargarSalidasProduccion()
  }, [codAlmacenSeleccionado])

  React.useEffect(() => {
    const refreshTimeoutRef = { current: null }

    const refreshPlannerDebounced = () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current)
      }

      refreshTimeoutRef.current = window.setTimeout(() => {
        refrescarPlanificador({ silent: true })
      }, 300)
    }

    const handleConnect = () => {
      console.log('Socket conectado', socket.id)
    }

    const handleDisconnect = (reason) => {
      console.log('Socket desconectado', reason)
    }

    const handleConnectError = (error) => {
      console.error('Error socket', error)
    }

    const handleConexionEstado = (payload) => {
      console.log('Estado conexion socket', payload)
    }

    refrescarPlanificador()

    if (!socket.connected) {
      socket.connect()
    }

    socket.on('connect', handleConnect)
    socket.on('disconnect', handleDisconnect)
    socket.on('connect_error', handleConnectError)
    socket.on('conexion.estado', handleConexionEstado)
    socket.on('planificacion.actualizada', refreshPlannerDebounced)
    socket.on('planificacion.eliminada', refreshPlannerDebounced)
    socket.on('pedido-linea.actualizada', refreshPlannerDebounced)
    socket.on('pedido-linea.produccion-actualizada', refreshPlannerDebounced)
    socket.on('tablero.refrescar', refreshPlannerDebounced)

    return () => {
      if (refreshTimeoutRef.current) {
        window.clearTimeout(refreshTimeoutRef.current)
      }

      socket.off('connect', handleConnect)
      socket.off('disconnect', handleDisconnect)
      socket.off('connect_error', handleConnectError)
      socket.off('conexion.estado', handleConexionEstado)
      socket.off('planificacion.actualizada', refreshPlannerDebounced)
      socket.off('planificacion.eliminada', refreshPlannerDebounced)
      socket.off('pedido-linea.actualizada', refreshPlannerDebounced)
      socket.off('pedido-linea.produccion-actualizada', refreshPlannerDebounced)
      socket.off('tablero.refrescar', refreshPlannerDebounced)
    }
  }, [refrescarPlanificador])

  const guardarPlanificacionDesdePayload = React.useCallback(async (payload, line, fechaInicio) => {
    if (!puedeModificarPlanificacion) return

    if (!payload || !line || !fechaInicio) return

    setDropPreview(null)
    setErrorPlanificacion(null)
    setGuardandoPlanificacion(true)

    try {
      if (payload.type === 'pedido') {
        await postPlanificacion({
          codMaquina: line.codMaquina,
          numPedido: payload.numPedido,
          numLinea: payload.numLinea,
          cantidadPales: payload.cantidadPales,
          fechaInicio,
        })
      }

      if (payload.type === 'planificacion') {
        await patchPlanificacion(payload.idPlanificacion, {
          codMaquina: line.codMaquina,
          cantidadPales: payload.cantidadPales,
          fechaInicio,
        })
      }

      if (payload.type === 'split-palets') {
        await patchPlanificacion(payload.idPlanificacion, {
          codMaquina: payload.codMaquinaOrigen,
          cantidadPales: payload.cantidadRestante,
          fechaInicio: payload.fechaInicioOriginal,
        })

        await postPlanificacion({
          codMaquina: line.codMaquina,
          numPedido: payload.numPedido,
          numLinea: payload.numLinea,
          cantidadPales: payload.cantidadPales,
          fechaInicio,
        })
      }

      await refrescarPlanificador({ silent: true })
    } catch (error) {
      console.error('Error al guardar la planificacion:', error)
      setErrorPlanificacion(getApiErrorInfo(error))
    } finally {
      setGuardandoPlanificacion(false)
    }
  }, [puedeModificarPlanificacion, refrescarPlanificador])

  const handleDropPlanificacion = React.useCallback(async (event, line) => {
    event.preventDefault()
    event.stopPropagation()

    if (!puedeModificarPlanificacion) return

    const payload = getDragPayload(event)
    const timelineElement = event.currentTarget

    if (!payload || !timelineElement) return

    const fechaInicio = getDropDate(event, timelineElement, timelineWindow, timelineWidth, visibleHours).toISOString()

    await guardarPlanificacionDesdePayload(payload, line, fechaInicio)
  }, [guardarPlanificacionDesdePayload, puedeModificarPlanificacion, timelineWidth, timelineWindow, visibleHours])

  const handleDragOverPlanificacion = React.useCallback((event, line) => {
    if (!puedeModificarPlanificacion) return

    event.preventDefault()

    const fechaInicio = getDropDate(event, event.currentTarget, timelineWindow, timelineWidth, visibleHours)
    const leftPercent = toTimelinePercent(fechaInicio, timelineWindow.start, visibleHours)
    const payload = getDragPayload(event)

    setDropPreview({
      codMaquina: line.codMaquina,
      left: `${leftPercent}%`,
      width: `${((DROP_SNAP_MINUTES / 60) / visibleHours) * 100}%`,
      label: payload?.type === 'split-palets'
        ? `${payload.cantidadPales} palets`
        : hourFormatter.format(fechaInicio),
    })
  }, [puedeModificarPlanificacion, timelineWidth, timelineWindow, visibleHours])

  const handlePedidoTouchPointerDown = React.useCallback((event, payload) => {
    if (!puedeModificarPlanificacion || event.pointerType === 'mouse') return

    touchPedidoDragState.current = {
      isPointerDown: true,
      isDragging: false,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      payload,
    }
  }, [puedeModificarPlanificacion])

  React.useEffect(() => {
    const resetTouchPedidoDrag = () => {
      touchPedidoDragState.current = {
        isPointerDown: false,
        isDragging: false,
        pointerId: null,
        startX: 0,
        startY: 0,
        payload: null,
      }
      setTouchPedidoDragPreview(null)
      setDropPreview(null)
    }

    const getDropzoneFromPoint = (clientX, clientY) => {
      const element = document.elementFromPoint(clientX, clientY)

      return element?.closest?.('[data-planner-line-dropzone="true"]') || null
    }

    const handlePointerMove = (event) => {
      const state = touchPedidoDragState.current

      if (!state.isPointerDown || state.pointerId !== event.pointerId || !state.payload) return

      const deltaX = event.clientX - state.startX
      const deltaY = event.clientY - state.startY

      if (!state.isDragging) {
        const movimientoHorizontal = Math.abs(deltaX) > TOUCH_DRAG_THRESHOLD_PX
          && Math.abs(deltaX) > Math.abs(deltaY) * 1.1

        if (!movimientoHorizontal) return

        touchPedidoDragState.current = {
          ...state,
          isDragging: true,
        }
      }

      event.preventDefault()

      const dropzone = getDropzoneFromPoint(event.clientX, event.clientY)

      setTouchPedidoDragPreview({
        x: event.clientX,
        y: event.clientY,
        label: `${state.payload.numPedido} | ${state.payload.cantidadPales} palets`,
      })

      if (!dropzone) {
        setDropPreview(null)
        return
      }

      const codMaquina = dropzone.dataset.codMaquina
      const fechaInicio = getDropDate(event, dropzone, timelineWindow, timelineWidth, visibleHours)
      const leftPercent = toTimelinePercent(fechaInicio, timelineWindow.start, visibleHours)

      setDropPreview({
        codMaquina,
        left: `${leftPercent}%`,
        width: `${((DROP_SNAP_MINUTES / 60) / visibleHours) * 100}%`,
        label: `${state.payload.cantidadPales} palets`,
      })
    }

    const handlePointerUp = async (event) => {
      const state = touchPedidoDragState.current

      if (!state.isPointerDown || state.pointerId !== event.pointerId) return

      const payload = state.payload
      const wasDragging = state.isDragging
      const dropzone = wasDragging ? getDropzoneFromPoint(event.clientX, event.clientY) : null

      resetTouchPedidoDrag()

      if (!wasDragging || !payload || !dropzone) return

      event.preventDefault()

      const line = productionLines.find((productionLine) => (
        String(productionLine.codMaquina) === String(dropzone.dataset.codMaquina)
      ))

      if (!line) return

      const fechaInicio = getDropDate(event, dropzone, timelineWindow, timelineWidth, visibleHours).toISOString()

      await guardarPlanificacionDesdePayload(payload, line, fechaInicio)
    }

    const handlePointerCancel = (event) => {
      const state = touchPedidoDragState.current

      if (state.pointerId !== event.pointerId) return

      resetTouchPedidoDrag()
    }

    window.addEventListener('pointermove', handlePointerMove, { passive: false })
    window.addEventListener('pointerup', handlePointerUp)
    window.addEventListener('pointercancel', handlePointerCancel)

    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
      window.removeEventListener('pointercancel', handlePointerCancel)
    }
  }, [guardarPlanificacionDesdePayload, productionLines, timelineWidth, timelineWindow, visibleHours])

  const handleDropPedidosPendientes = React.useCallback(async (event) => {
    event.preventDefault()
    event.stopPropagation()

    if (!puedeModificarPlanificacion) return

    const payload = getDragPayload(event)

    if (!payload || payload.type !== 'planificacion') return

    setDropPreview(null)
    setErrorPlanificacion(null)
    setGuardandoPlanificacion(true)

    try {
      await deletePlanificacion(payload.idPlanificacion)
      await refrescarPlanificador({ silent: true })
    } catch (error) {
      console.error('Error al quitar la planificacion:', error)
      setErrorPlanificacion(getApiErrorInfo(error))
    } finally {
      setGuardandoPlanificacion(false)
    }
  }, [puedeModificarPlanificacion, refrescarPlanificador])

  const handleSplitPlanificacion = React.useCallback(async () => {
    if (!puedeModificarPlanificacion) return
    if (!splitDialog?.task) return

    const cantidadPrimera = Number(splitDialog.cantidadPrimera)
    const task = splitDialog.task
    const cantidadTotal = Number(task.cantidadPales)

    if (!Number.isFinite(cantidadPrimera) || cantidadPrimera <= 0 || cantidadPrimera >= cantidadTotal) return

    const cantidadSegunda = cantidadTotal - cantidadPrimera
    const codMaquinaDestino = splitDialog.codMaquinaDestino || task.codMaquina
    const segundaEnOtraMaquina = String(codMaquinaDestino) !== String(task.codMaquina)

    setGuardandoPlanificacion(true)
    setErrorPlanificacion(null)

    try {
      const planificacionActualizada = await patchPlanificacion(task.idPlanificacion, {
        codMaquina: task.codMaquina,
        cantidadPales: cantidadPrimera,
        fechaInicio: task.startDate.toISOString(),
      })

      await postPlanificacion({
        codMaquina: codMaquinaDestino,
        numPedido: task.numPedido,
        numLinea: task.numLinea,
        cantidadPales: cantidadSegunda,
        fechaInicio: segundaEnOtraMaquina
          ? task.startDate.toISOString()
          : getFechaFinPlanificacion(planificacionActualizada) || task.endDate.toISOString(),
      })

      setSplitDialog(null)
      await refrescarPlanificador({ silent: true })
    } catch (error) {
      console.error('Error al dividir la planificacion:', error)
      setErrorPlanificacion(getApiErrorInfo(error))
    } finally {
      setGuardandoPlanificacion(false)
    }
  }, [puedeModificarPlanificacion, refrescarPlanificador, splitDialog])

  const stopTimelineDrag = React.useCallback((event) => {
    const timelineScroll = timelineScrollRef.current
    const state = dragScrollState.current

    if (!state.isDragging) return
    if (event && state.pointerId !== event.pointerId) return

    if (timelineScroll && state.pointerId !== null) {
      timelineScroll.releasePointerCapture?.(state.pointerId)
    }

    dragScrollState.current = {
      isDragging: false,
      pointerId: null,
      startX: 0,
      scrollLeft: 0,
    }
    setIsTimelineDragging(false)
  }, [])

  const handleTimelinePointerDown = React.useCallback((event) => {
    if (event.button !== 0) return

    const timelineScroll = timelineScrollRef.current

    if (!timelineScroll) return

    dragScrollState.current = {
      isDragging: true,
      pointerId: event.pointerId,
      startX: event.clientX,
      scrollLeft: timelineScroll.scrollLeft,
    }
    timelineScroll.setPointerCapture?.(event.pointerId)
    setIsTimelineDragging(true)
  }, [])

  const handleTimelinePointerMove = React.useCallback((event) => {
    const timelineScroll = timelineScrollRef.current
    const state = dragScrollState.current

    if (!timelineScroll || !state.isDragging || state.pointerId !== event.pointerId) return

    timelineScroll.scrollLeft = state.scrollLeft - (event.clientX - state.startX)
    event.preventDefault()
  }, [])

  React.useEffect(() => {
    const timelineScroll = timelineScrollRef.current

    if (!timelineScroll || loadingSalidas || loadingPlanificaciones) return

    if (!currentTimeVisible) {
      timelineScroll.scrollLeft = 0
      return
    }

    const currentLineTimelineX = (timelineWidth * currentTimePercent) / 100
    const maxScrollLeft = Math.max(timelineScroll.scrollWidth - timelineScroll.clientWidth, 0)

    timelineScroll.scrollLeft = clamp(currentLineTimelineX - CURRENT_TIME_SCROLL_LEAD_PX, 0, maxScrollLeft)
  }, [currentTimePercent, currentTimeVisible, diaPlanificador, loadingPlanificaciones, loadingSalidas, productionLines.length, timelineWidth, visibleHours])

  const splitTotalPalets = Number(splitDialog?.task?.cantidadPales ?? 0)
  const splitPaletsPrimeraRaw = Number(splitDialog?.cantidadPrimera ?? 0)
  const splitPaletsPrimera = Math.min(
    Math.max(Number.isFinite(splitPaletsPrimeraRaw) ? splitPaletsPrimeraRaw : 0, 0),
    splitTotalPalets
  )
  const splitPaletsSegunda = Math.max(splitTotalPalets - splitPaletsPrimera, 0)
  const splitDivisionInvalida = splitPaletsPrimera <= 0 || splitPaletsPrimera >= splitTotalPalets
  const detalleData = detallePedido?.data
  const detallePedidoTitulo = detallePedido
    ? `${detalleData?.numPedido ?? detalleData?.code ?? '-'}${detalleData?.numLinea ? ` / Linea ${detalleData.numLinea}` : ''}`
    : ''
  const detallePedidoBlocks = React.useMemo(() => {
    if (!detallePedido?.data) return []

    const data = detallePedido.data
    const pedidoRaw = data.pedidoRaw || {}
    const pedido = data.pedido || data.planificacionRaw?.pedido || {}
    const cabecera = detallePedido.tipo === 'planificacion'
      ? (data.cabeceraPedido || pedido.cabecera || pedidoRaw.pedido?.cabecera || pedidoRaw.cabecera || {})
      : (pedido.cabecera || data.cabecera || data.cabeceraPedido || data)
    const confeccion = detallePedido.tipo === 'planificacion'
      ? (data.confeccionPedido || pedido.confeccion || pedidoRaw.pedido?.confeccion || pedidoRaw.confeccion || {})
      : (pedido.confeccion || data.confeccion || data.confeccionPedido || {})
    const lineaBase = detallePedido.tipo === 'planificacion'
      ? (data.lineaPedido || pedido.linea || pedidoRaw.pedido?.linea || pedidoRaw.linea || {})
      : (pedido.linea || data.linea || data.lineaPedido || data)
    const detalle = {
      ...data,
      ...cabecera,
      ...lineaBase,
      desConfeccion: data.desConfeccion ?? confeccion?.desConfeccion ?? lineaBase?.desConfeccion,
      numPedido: data.numPedido ?? data.code ?? cabecera?.numPedido ?? lineaBase?.numPedido,
      desCliente: data.desCliente ?? data.cliente ?? cabecera?.desCliente ?? lineaBase?.desCliente,
      fechaCarga: data.fechaCarga ?? cabecera?.fechaCarga,
      desDirEnvio: data.desDirEnvio ?? cabecera?.desDirEnvio ?? lineaBase?.desDirEnvio,
      sp: data.sp ?? cabecera?.sp,
      observacionesPedido: data.observacionesPedido ?? cabecera?.observacionesPedido,
      observacionesCarga: data.observacionesCarga ?? cabecera?.observacionesCarga,
      desVariedad: data.desVariedad ?? data.title ?? lineaBase?.desVariedad,
      numLote: data.numLote ?? lineaBase?.numLote,
      desCalibre: data.desCalibre ?? data.calibre ?? lineaBase?.desCalibre,
      desEnvase: data.desEnvase ?? lineaBase?.desEnvase,
      desPalet: data.desPalet ?? lineaBase?.desPalet,
      desMarca: data.desMarca ?? lineaBase?.desMarca,
      ggn: data.ggn ?? lineaBase?.ggn,
      desCategoria: data.desCategoria ?? lineaBase?.desCategoria,
      observacion: data.observacion ?? lineaBase?.observacion,
      cantidadPales: data.cantidadPales ?? lineaBase?.cantidadPales,
      bultosPalet: data.bultosPalet ?? lineaBase?.bultosPalet,
    }

    return [
      {
        title: 'Detalle pedido',
        data: detalle,
        fields: detallePedidoFields,
      },
    ]
      .filter((block) => tieneDatosDetalle(block.data, block.fields))
      .map((block) => ({
        title: block.title,
        rows: getPedidoDetailRows(block.data, block.fields),
      }))
  }, [detallePedido])

  const detallePedidoCambios = React.useMemo(() => {
    if (!detallePedido?.data) return []

    const data = detallePedido.data

    if (detallePedido.tipo === 'planificacion') {
      return deduplicarCambiosDetalle(getCambiosDesdeObjeto(data.planificacionRaw || data))
    }

    return deduplicarCambiosDetalle(getCambiosDesdeObjeto(data))
  }, [detallePedido])

  const abrirDetallePlanificacion = React.useCallback((task) => {
    const cambios = deduplicarCambiosDetalle(getCambiosDesdeObjeto(task.planificacionRaw || task))
    const firma = getCambiosFirma(cambios)

    if (firma) {
      setCambioVistoFirma(getCambiosPlanificacionKey(task), firma)
      setCambiosVistosVersion((version) => version + 1)
    }

    setDetallePedido({ tipo: 'planificacion', data: task })
  }, [])

  return (
    <Box sx={{ height: 'calc(100vh - 52px)', maxWidth: '100%', minWidth: '100%', overflow: 'hidden' }}>
      <Box
        className='fade-in'
        sx={{
          px: { xs: 1, md: 1.5 },
          py: { xs: 1, md: 1.5 },
          height: '100%',
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            width: '100%',
            maxWidth: '100%',
            height: '100%',
            mx: 'auto',
            position: 'relative',
            border: '1px solid #DEE6F2',
            borderRadius: '8px',
            overflow: 'hidden',
            backgroundColor: '#EEEEEE',
            backdropFilter: 'blur(12px)',
            boxShadow: '0 30px 80px rgba(15, 23, 42, 0.12)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <PlannerTopBar
            summaryCards={summaryCardsPlanificador}
            legendItems={legendItems}
            visibleHours={visibleHours}
            setVisibleHours={setVisibleHours}
            diaPlanificador={diaPlanificador}
            setDiaPlanificador={setDiaPlanificador}
            addDaysToDateValue={addDaysToDateValue}
            toDateInputValue={toDateInputValue}
            minVisibleHours={MIN_VISIBLE_HOURS}
            maxVisibleHours={MAX_VISIBLE_HOURS}
            zoomStepHours={ZOOM_STEP_HOURS}
            vistaCompacta={vistaCompacta}
            setVistaCompacta={setVistaCompacta}
          />

          <Box sx={{ position: 'relative', flex: 1, minHeight: 0 }}>
            <PedidosPendientesPanel
              variant="side"
              pedidosContraidos={pedidosContraidos}
              setPedidosContraidos={setPedidosContraidos}
              loadingPedidos={loadingPedidos}
              pedidosPendientes={pedidosPendientesPanel}
              coloresPorVariedad={coloresPorVariedad}
              totalPedidosPendientes={pedidosPendientesPanel.length}
              gruposConfeccion={gruposConfeccion}
              grupoConfeccionFiltro={grupoConfeccionFiltro}
              setGrupoConfeccionFiltro={setGrupoConfeccionFiltro}
              handleDropPedidosPendientes={handleDropPedidosPendientes}
              getCantidadPales={getCantidadPales}
              getPaletsPlanificados={getPaletsPlanificados}
              getPaletsProducidos={getPaletsProducidos}
              getPaletsPendientesPlanificar={getPaletsPendientesPlanificar}
              setDragPayload={setDragPayload}
              setDropPreview={setDropPreview}
              setDetallePedido={setDetallePedido}
              onPedidoTouchPointerDown={handlePedidoTouchPointerDown}
              puedeModificarPlanificacion={puedeModificarPlanificacion}
            />

            <Box
            ref={timelineScrollRef}
            onPointerDown={handleTimelinePointerDown}
            onPointerMove={handleTimelinePointerMove}
            onPointerUp={stopTimelineDrag}
            onPointerCancel={stopTimelineDrag}
            onLostPointerCapture={stopTimelineDrag}
            onDragStart={(event) => {
              if (!event.target.closest('[data-planner-draggable="true"]')) {
                event.preventDefault()
              }
            }}
            sx={{
              height: '100%',
              minHeight: 0,
              overflow: 'auto',
              backgroundColor: '#F7FAFF',
              cursor: isTimelineDragging ? 'grabbing' : 'grab',
              touchAction: 'pan-y',
              userSelect: isTimelineDragging ? 'none' : 'auto',
            }}
          >
            {loadingSalidas || loadingPlanificaciones ? (
              <Box sx={{ minWidth: '100%', height: '100%', display: 'grid', placeItems: 'center' }}>
                <LoadingMaestro label="Cargando planificacion..." />
              </Box>
            ) : productionLines.length === 0 ? (
              <Box sx={{ minWidth: '100%', height: '100%', display: 'grid', placeItems: 'center', px: 2 }}>
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: '8px',
                    border: '1px solid #DEE6F2',
                    backgroundColor: '#FFFFFF',
                    textAlign: 'center',
                  }}
                >
                  <Typography sx={{ color: '#172033', fontWeight: 700, fontSize: '0.95rem' }}>
                    Sin maquinas activas para el centro seleccionado
                  </Typography>
                  <Typography sx={{ color: '#64748B', fontSize: '0.78rem', mt: 0.4 }}>
                    Cambia el centro o revisa las salidas de produccion en ajustes.
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Box sx={{ minWidth: labelWidth + timelineWidth }}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 8,
                    display: 'grid',
                    gridTemplateColumns: `${labelWidth}px ${timelineWidth}px`,
                    borderBottom: '1px solid #E6ECF5',
                    backgroundColor: '#FCFDFE',
                  }}
                >
                  <Box
                    sx={{
                      position: 'sticky',
                      left: 0,
                      zIndex: 3,
                      px: 2,
                      py: 2,
                      display: 'flex',
                      alignItems: 'center',
                      borderRight: '1px solid #E6ECF5',
                      backgroundColor: '#FCFDFE',
                      fontWeight: 800,
                      color: '#8CA0C1',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      fontSize: '0.82rem',
                    }}
                  >
                  Salidas de produccion
                  </Box>

                  <Box
                    sx={{
                      position: 'relative',
                      height: 58,
                      backgroundImage: timelineGridBackground,
                    }}
                  >
                    {currentTimeVisible && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${currentTimePercent}%`,
                          top: 0,
                          bottom: 0,
                          width: '2px',
                          backgroundColor: '#F57C00',
                        }}
                      />
                    )}

                    {daySegments.map((segment, index) => (
                      <Box
                        key={`${segment.label}-${index}`}
                        sx={{
                          position: 'absolute',
                          left: segment.left,
                          top: 0,
                          width: segment.width,
                          height: 20,
                          borderRight: '1px solid #E6ECF5',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#64748B',
                            fontWeight: 800,
                            fontSize: '0.62rem',
                            textTransform: 'uppercase',
                            lineHeight: 1,
                          }}
                        >
                          {segment.label}
                        </Typography>
                      </Box>
                    ))}

                    {currentTimeVisible && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${currentTimePercent}%`,
                          top: 38,
                          transform: 'translateX(-50%)',
                          width: 12,
                          height: 12,
                          borderRadius: '8px',
                          backgroundColor: '#F57C00',
                          boxShadow: '0 0 0 6px rgba(245, 124, 0, 0.14)',
                        }}
                      />
                    )}

                    {hourMarks.map((mark, index) => (
                      <Box
                        key={index}
                        sx={{
                          position: 'absolute',
                          top: 25,
                          left: `${(index / (visibleHours * 4)) * 100}%`,
                          transform:
                            index === 0
                              ? 'translateX(0)'
                              : index === hourMarks.length - 1
                                ? 'translateX(-100%)'
                                : 'translateX(-50%)',
                          textAlign: index === 0 ? 'left' : index === hourMarks.length - 1 ? 'right' : 'center',
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#8CA0C1',
                            fontWeight: 800,
                            fontSize: mark.isHour ? '0.72rem' : '0.62rem',
                            lineHeight: 1.1,
                          }}
                        >
                          {mark.time}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {productionLines.map((line, lineIndex) => {
                  const colorLineaTenue = getColorMaquinaTenue(line.colorMaquina)
                  const tieneColorLinea = Boolean(hexToRgba(line.colorMaquina, 1))

                  return (
                  <Box
                    key={line.name}
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: `${labelWidth}px ${timelineWidth}px`,
                      borderBottom:
                        lineIndex === productionLines.length - 1
                          ? 'none'
                          : '1px solid #E6ECF5',
                    }}
                  >
                    <Box
                      sx={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 7,
                        px: vistaCompacta ? 1 : 1.5,
                        py: vistaCompacta ? 0.45 : 0.8,
                        backgroundColor: tieneColorLinea ? colorLineaTenue : '#FFFFFF',
                        borderRight: '1px solid #E6ECF5',
                        boxShadow: tieneColorLinea ? `inset 4px 0 0 ${line.colorMaquina}` : 'none',
                        display: 'flex',
                        flexDirection: vistaCompacta ? 'row' : 'column',
                        justifyContent: vistaCompacta ? 'space-between' : 'center',
                        alignItems: vistaCompacta ? 'center' : 'stretch',
                        gap: vistaCompacta ? 0.75 : 0.4,
                      }}
                    >
                      <Typography
                        sx={{
                          fontWeight: 500,
                          color: '#1F2937',
                          fontSize: vistaCompacta ? '0.74rem' : '0.86rem',
                          lineHeight: 1.15,
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {line.name}
                      </Typography>

                    {!vistaCompacta && line.area && (
                      <Typography sx={{ color: '#64748B', fontSize: '0.68rem', lineHeight: 1.2 }}>
                        {line.area}
                      </Typography>
                    )}

                      <Box
                        sx={{
                          alignSelf: 'flex-start',
                          px: vistaCompacta ? 0.55 : 0.8,
                          py: vistaCompacta ? 0.15 : 0.25,
                          borderRadius: '999px',
                          backgroundColor: '#EEF5FF',
                          color: '#315B99',
                          fontWeight: 700,
                          fontSize: vistaCompacta ? '0.54rem' : '0.62rem',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {line.utilization}
                      </Box>
                    </Box>

                    <Box
                      data-planner-line-dropzone="true"
                      data-cod-maquina={line.codMaquina}
                      onDragOver={puedeModificarPlanificacion ? (event) => handleDragOverPlanificacion(event, line) : undefined}
                      onDrop={puedeModificarPlanificacion ? (event) => handleDropPlanificacion(event, line) : undefined}
                      sx={{
                        position: 'relative',
                        height: rowHeight,
                        backgroundImage: getTimelineGridBackground(colorLineaTenue),
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${currentTimePercent}%`,
                          top: 0,
                          bottom: 0,
                          width: '2px',
                          backgroundColor: '#F57C00',
                        }}
                      />

                      {String(dropPreview?.codMaquina) === String(line.codMaquina) && (
                        <Box
                          sx={{
                            position: 'absolute',
                            left: dropPreview.left,
                            top: 0,
                            bottom: 0,
                            width: dropPreview.width,
                            minWidth: 16,
                            borderLeft: '2px solid #F57C00',
                            borderRight: '2px solid #F57C00',
                            backgroundColor: 'rgba(245, 124, 0, 0.10)',
                            pointerEvents: 'none',
                            zIndex: 1,
                          }}
                        >
                          <Box
                            sx={{
                              position: 'absolute',
                              top: 4,
                              left: 4,
                              px: 0.6,
                              py: 0.2,
                              borderRadius: '6px',
                              backgroundColor: '#F57C00',
                              color: '#FFFFFF',
                              fontSize: '0.62rem',
                              fontWeight: 900,
                              lineHeight: 1,
                            }}
                          >
                            {dropPreview.label}
                          </Box>
                        </Box>
                      )}

                      {line.tasks.map((task) => {
                        const position = getTaskPosition(
                          task.startDate,
                          task.endDate,
                          timelineWindow.start,
                          timelineWindow.end
                        )

                        if (!position) return null

                        const isCancelled = task.eliminadoBusinessCentral || task.status === 'cancelled'
                        const tone = statusStyles[task.status] || statusStyles.waiting
                        const taskCambios = deduplicarCambiosDetalle(getCambiosDesdeObjeto(task.planificacionRaw || task))
                        const taskCambiosFirma = getCambiosFirma(taskCambios)
                        const mostrarAvisoCambios = Boolean(
                          taskCambiosFirma
                          && getCambioVistoFirma(getCambiosPlanificacionKey(task)) !== taskCambiosFirma
                        )
                        const paletsVisuales = getPaletsVisualesTimeline(task)

                        return (
                          <Box
                            key={`${line.name}-${task.key}`}
                            data-planner-draggable="true"
                            draggable={puedeModificarPlanificacion}
                            onPointerDown={(event) => event.stopPropagation()}
                            onDragStart={(event) => {
                              if (!puedeModificarPlanificacion) {
                                event.preventDefault()
                                return
                              }

                              setDragPayload(event, {
                                type: 'planificacion',
                                idPlanificacion: task.idPlanificacion,
                                cantidadPales: task.cantidadPales,
                              })
                            }}
                            onDragEnd={() => setDropPreview(null)}
                            onDoubleClick={(event) => {
                              event.stopPropagation()
                              abrirDetallePlanificacion(task)
                            }}
                            sx={{
                              position: 'absolute',
                              top: taskCardTop,
                              left: position.left,
                              width: position.width,
                              minWidth: 0,
                              maxWidth: 'calc(100% - 16px)',
                              height: taskCardHeight,
                              px: vistaCompacta ? 0.8 : 1.5,
                              py: vistaCompacta ? 0.35 : 0.9,
                              boxSizing: 'border-box',
                              borderRadius: vistaCompacta ? '6px' : '8px',
                              border: tone.border,
                              background: tone.background,
                              boxShadow: tone.shadow,
                              opacity: isCancelled ? 0.94 : 1,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: vistaCompacta ? 'center' : 'stretch',
                              gap: vistaCompacta ? 0.75 : 1.5,
                              overflow: 'hidden',
                              cursor: puedeModificarPlanificacion ? 'grab' : 'default',
                              '&:active': {
                                cursor: puedeModificarPlanificacion ? 'grabbing' : 'default',
                              },
                            }}
                          >
                          {mostrarAvisoCambios && vistaCompacta && (
                            <Tooltip title="Pedido con cambios pendientes de revisar" arrow placement="top">
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: 3,
                                  right: 4,
                                  width: 18,
                                  height: 18,
                                  borderRadius: '999px',
                                  backgroundColor: '#FFF7ED',
                                  color: '#C2410C',
                                  border: '1px solid rgba(194, 65, 12, 0.28)',
                                  display: 'grid',
                                  placeItems: 'center',
                                  boxShadow: '0 2px 5px rgba(15, 23, 42, 0.18)',
                                  pointerEvents: 'auto',
                                  zIndex: 8,
                                }}
                              >
                                <NotificationsActiveRoundedIcon sx={{ fontSize: 12 }} />
                              </Box>
                            </Tooltip>
                          )}

                          {isCancelled && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                height: 3,
                                backgroundColor: tone.accent,
                                pointerEvents: 'none',
                                zIndex: 6,
                              }}
                            />
                          )}

                          {!vistaCompacta && (
                            <>
                          {paletsVisuales.map((palet) => {
                            if (!palet.producidoTimeline) return null

                            const paletInfo = getPaletDisplayInfo(palet)
                            const { left, width } = getPaletBoundsPercent(palet, task)

                            if (!Number.isFinite(left) || !Number.isFinite(width) || width <= 0) return null

                            const diferenciaNumerica = Number(paletInfo.diferencia)
                            const diferenciaColor = diferenciaNumerica > 0
                              ? { bg: '#FEE2E2', fg: '#B91C1C', label: 'Atr' }
                              : diferenciaNumerica < 0
                                ? { bg: '#DCFCE7', fg: '#15803D', label: 'Adel' }
                                : { bg: '#EAF8EF', fg: '#15803D', label: 'OK' }

                            return (
                              <Tooltip
                                key={`palet-info-${palet.idPlanificacionPalet ?? palet.numeroPalet}`}
                                  title={<Box sx={{ whiteSpace: 'pre-line' }}>{getPaletTooltip(palet)}</Box>}
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      bottom: 0,
                                      left: `${clamp(left, 0, 100)}%`,
                                      width: `${clamp(width, 0, 100)}%`,
                                      minWidth: 12,
                                      borderRight: '1px solid rgba(255, 255, 255, 0.34)',
                                    borderLeft: palet.numeroPalet === 1 ? 'none' : '1px solid rgba(15, 23, 42, 0.10)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    px: 0.25,
                                    pointerEvents: 'auto',
                                    textAlign: 'center',
                                    overflow: 'hidden',
                                    zIndex: 2,
                                  }}
                                >
                                  <Box
                                    sx={{
                                      maxWidth: 'calc(100% - 4px)',
                                      px: 0.45,
                                      py: 0.2,
                                      borderRadius: '999px',
                                      backgroundColor: diferenciaColor.bg,
                                      color: diferenciaColor.fg,
                                      fontSize: '0.52rem',
                                      fontWeight: 900,
                                      lineHeight: 1,
                                      whiteSpace: 'nowrap',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
                                    }}
                                  >
                                    {diferenciaColor.label} {paletInfo.diferenciaLabel}
                                  </Box>
                                </Box>
                              </Tooltip>
                              )
                            })}

                            {paletsVisuales.map((palet) => {
                              if (palet.producidoTimeline) return null

                              const { left, width } = getPaletBoundsPercent(palet, task)

                              if (!Number.isFinite(left) || !Number.isFinite(width) || width <= 0) return null

                              return (
                                <Tooltip
                                  key={`palet-pendiente-info-${palet.idPlanificacionPalet ?? palet.numeroPalet}`}
                                  title={<Box sx={{ whiteSpace: 'pre-line' }}>{getPaletPendienteTooltip(palet)}</Box>}
                                  arrow
                                  placement="top"
                                >
                                  <Box
                                    sx={{
                                      position: 'absolute',
                                      top: 0,
                                      bottom: 0,
                                      left: `${clamp(left, 0, 100)}%`,
                                      width: `${clamp(width, 0, 100)}%`,
                                      minWidth: 12,
                                      pointerEvents: 'auto',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      px: 0.25,
                                      overflow: 'hidden',
                                      zIndex: 1,
                                      backgroundColor: 'transparent',
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        maxWidth: 'calc(100% - 4px)',
                                        px: 0.45,
                                        py: 0.2,
                                        borderRadius: '999px',
                                        backgroundColor: 'rgba(255, 255, 255, 0.88)',
                                        color: '#475569',
                                        fontSize: '0.52rem',
                                        fontWeight: 900,
                                        lineHeight: 1,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        boxShadow: '0 1px 2px rgba(15, 23, 42, 0.14)',
                                        border: '1px solid rgba(148, 163, 184, 0.28)',
                                      }}
                                    >
                                      Fin {formatTime(getFechaFinPrevistaCalculadaPalet(palet))}
                                    </Box>
                                  </Box>
                                </Tooltip>
                              )
                            })}

                            {paletsVisuales.map((palet) => {
                              if (!palet.producidoTimeline) return null

                              const { left, width } = getPaletBoundsPercent(palet, task)

                              if (!Number.isFinite(left) || !Number.isFinite(width) || width <= 0) return null

                              return (
                                <Box
                                  key={`producido-${palet.idPlanificacionPalet ?? palet.numeroPalet}`}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: `${clamp(left, 0, 100)}%`,
                                    width: `${clamp(width, 0, 100)}%`,
                                    backgroundColor: 'rgba(22, 163, 74, 0.28)',
                                    borderRight: '1px solid rgba(21, 128, 61, 0.35)',
                                    pointerEvents: 'none',
                                  }}
                                />
                              )
                            })}

                            {paletsVisuales.slice(1).map((palet, paletIndex) => {
                              const paletOffset = Number(palet.timelineLeft)
                              const cantidadPalesIzquierda = paletIndex + 1
                              const cantidadTotalTask = Number(task.cantidadPales ?? paletsVisuales.length)
                              const cantidadRestante = cantidadTotalTask - cantidadPalesIzquierda
                              const puedeDividirDesdeSeparador = puedeModificarPlanificacion
                                && !isCancelled
                                && cantidadPalesIzquierda > 0
                                && cantidadRestante > 0

                              if (!Number.isFinite(paletOffset) || paletOffset <= 0 || paletOffset >= 100) return null

                              return (
                                <Box
                                  key={palet.idPlanificacionPalet ?? palet.numeroPalet}
                                  data-planner-draggable="true"
                                  draggable={puedeDividirDesdeSeparador}
                                  title={puedeDividirDesdeSeparador ? `Arrastrar ${cantidadPalesIzquierda} palets a otra linea` : undefined}
                                  onPointerDown={(event) => {
                                    if (puedeDividirDesdeSeparador) event.stopPropagation()
                                  }}
                                  onDoubleClick={(event) => event.stopPropagation()}
                                  onDragStart={(event) => {
                                    if (!puedeDividirDesdeSeparador) {
                                      event.preventDefault()
                                      return
                                    }

                                    event.stopPropagation()
                                    setDragPayload(event, {
                                      type: 'split-palets',
                                      idPlanificacion: task.idPlanificacion,
                                      codMaquinaOrigen: task.codMaquina,
                                      numPedido: task.numPedido,
                                      numLinea: task.numLinea,
                                      cantidadPales: cantidadPalesIzquierda,
                                      cantidadRestante,
                                      fechaInicioOriginal: task.startDate.toISOString(),
                                    })
                                  }}
                                  onDragEnd={(event) => {
                                    event.stopPropagation()
                                    setDropPreview(null)
                                  }}
                                  sx={{
                                    position: 'absolute',
                                    top: 0,
                                    bottom: 0,
                                    left: `calc(${paletOffset}% - 3px)`,
                                    width: '6px',
                                    cursor: puedeDividirDesdeSeparador ? 'grab' : 'default',
                                    pointerEvents: puedeDividirDesdeSeparador ? 'auto' : 'none',
                                    zIndex: 7,
                                    display: 'flex',
                                    justifyContent: 'center',
                                    '&::after': {
                                      content: '""',
                                      width: '2px',
                                      height: '100%',
                                      borderRadius: '999px',
                                      backgroundColor: task.status === 'active'
                                        ? 'rgba(255, 255, 255, 0.62)'
                                        : 'rgba(71, 85, 105, 0.42)',
                                      boxShadow: puedeDividirDesdeSeparador
                                        ? '0 0 0 1px rgba(255, 255, 255, 0.38)'
                                        : 'none',
                                    },
                                    '&:hover::after': {
                                      width: '4px',
                                      backgroundColor: '#F57C00',
                                      boxShadow: '0 0 0 3px rgba(245, 124, 0, 0.18)',
                                    },
                                    '&:active': {
                                      cursor: puedeDividirDesdeSeparador ? 'grabbing' : 'default',
                                    },
                                  }}
                                />
                              )
                            })}
                            </>
                          )}

                            <Box sx={{ minWidth: 0, flex: 1 }}>
                              {vistaCompacta && (
                                <Typography
                                  sx={{
                                    color: tone.titleColor,
                                    fontWeight: 800,
                                    fontSize: '0.68rem',
                                    lineHeight: 1,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {task.code || '-'} | {task.desCliente || '-'} | <VariedadLabel color={coloresPorVariedad[task.codVariedad]}>{task.desVariedad || '-'}</VariedadLabel> | {task.desConfeccion || '-'} | {task.desMarca || '-'}
                                </Typography>
                              )}

                              {!vistaCompacta && (
                                <>
                              {task.code && (
                                <Typography
                                  sx={{
                                    color: tone.metaColor,
                                    fontWeight: 800,
                                    fontSize: '0.66rem',
                                    lineHeight: 1.05,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    mb: 0.25,
                                  }}
                                >
                                  {task.code}{isCancelled ? ' · Cancelado BC' : ''}
                                </Typography>
                              )}

                              <Typography
                                sx={{
                                  color: tone.titleColor,
                                  fontWeight: 700,
                                  fontSize: '0.78rem',
                                  lineHeight: 1.1,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {task.desCliente}
                              </Typography>

                              <Typography
                                sx={{
                                  color: tone.metaColor,
                                  fontWeight: 700,
                                  fontSize: '0.68rem',
                                  lineHeight: 1.1,
                                  mt: 0.25,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                <VariedadLabel color={coloresPorVariedad[task.codVariedad]}>{task.desVariedad}</VariedadLabel> | {task.desConfeccion}
                              </Typography>

                              <Typography
                                sx={{
                                  color: tone.noteColor,
                                  fontSize: '0.66rem',
                                  lineHeight: 1.1,
                                  mt: 0.25,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                Destino {task.desDirEnvio} | Marca {task.desMarca}
                              </Typography>

                              <Typography
                                sx={{
                                  color: tone.noteColor,
                                  fontSize: '0.66rem',
                                  fontWeight: 800,
                                  lineHeight: 1.1,
                                  mt: 0.25,
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                }}
                              >
                                {task.cantidadPales} palets | Fin {task.endLabel}
                              </Typography>

                              <Box
                                sx={{
                                  mt: 0.65,
                                  width: '100%',
                                  maxWidth: 220,
                                  height: 4,
                                  borderRadius: '999px',
                                  backgroundColor: tone.progressTrack,
                                  overflow: 'hidden',
                                }}
                              >
                                <Box
                                  sx={{
                                    width: `${task.progress}%`,
                                    height: '100%',
                                    borderRadius: 'inherit',
                                    backgroundColor: tone.progressFill,
                                  }}
                                />
                              </Box>
                                </>
                              )}
                            </Box>

                            <Box
                              sx={{
                                flexShrink: 0,
                                alignSelf: 'flex-start',
                                display: vistaCompacta ? 'none' : 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                position: 'relative',
                                zIndex: 5,
                              }}
                            >
                              {mostrarAvisoCambios && (
                                <Tooltip title="Pedido con cambios pendientes de revisar" arrow placement="top">
                                  <Box
                                    sx={{
                                      width: 25,
                                      height: 25,
                                      borderRadius: '999px',
                                      backgroundColor: '#FFF7ED',
                                      color: '#C2410C',
                                      border: '1px solid rgba(194, 65, 12, 0.28)',
                                      display: 'grid',
                                      placeItems: 'center',
                                      boxShadow: '0 1px 3px rgba(15, 23, 42, 0.14)',
                                      flexShrink: 0,
                                    }}
                                  >
                                    <NotificationsActiveRoundedIcon sx={{ fontSize: 15 }} />
                                  </Box>
                                </Tooltip>
                              )}

                              {puedeModificarPlanificacion && task.cantidadPales > 1 && (
                                <IconButton
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation()
                                    setSplitDialog({
                                      task,
                                      cantidadPrimera: Math.max(1, Math.floor(task.cantidadPales / 2)),
                                      codMaquinaDestino: task.codMaquina,
                                    })
                                  }}
                                  sx={{
                                    width: 25,
                                    height: 25,
                                    backgroundColor: tone.badgeBg,
                                    color: tone.badgeColor,
                                    '&:hover': {
                                      backgroundColor: tone.badgeBg,
                                    },
                                  }}
                                >
                                  <ContentCutRoundedIcon sx={{ fontSize: 14 }} />
                                </IconButton>
                              )}

                              {isCancelled && (
                                <Tooltip title="Pedido cancelado en Business Central" arrow placement="top">
                                  <Box
                                    sx={{
                                      px: 0.7,
                                      py: 0.35,
                                      borderRadius: '999px',
                                      backgroundColor: tone.cancelBg,
                                      color: tone.cancelColor,
                                      fontWeight: 900,
                                      fontSize: '0.62rem',
                                      lineHeight: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.35,
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    <BlockRoundedIcon sx={{ fontSize: 13 }} />
                                    Cancelado
                                  </Box>
                                </Tooltip>
                              )}

                              <Box
                                sx={{
                                  px: 0.85,
                                  py: 0.4,
                                  borderRadius: '999px',
                                  backgroundColor: tone.badgeBg,
                                  color: tone.badgeColor,
                                  fontWeight: 800,
                                  fontSize: '0.66rem',
                                  letterSpacing: '0.04em',
                                }}
                              >
                                {task.badge}
                              </Box>
                            </Box>
                          </Box>
                        )
                      })}
                    </Box>
                  </Box>
                  )
                })}
              </Box>
            )}
            </Box>
            {touchPedidoDragPreview && (
              <Box
                sx={{
                  position: 'fixed',
                  left: touchPedidoDragPreview.x + 14,
                  top: touchPedidoDragPreview.y + 14,
                  zIndex: 2500,
                  pointerEvents: 'none',
                  px: 1,
                  py: 0.55,
                  borderRadius: '8px',
                  backgroundColor: '#172033',
                  color: '#FFFFFF',
                  boxShadow: '0 10px 24px rgba(15, 23, 42, 0.24)',
                  fontSize: '0.72rem',
                  fontWeight: 800,
                  whiteSpace: 'nowrap',
                }}
              >
                {touchPedidoDragPreview.label}
              </Box>
            )}
          </Box>

        </Box>
      </Box>

      <SplitPlanificacionDialog
        splitDialog={splitDialog}
        setSplitDialog={setSplitDialog}
        salidasProduccion={salidasProduccion}
        handleSplitPlanificacion={handleSplitPlanificacion}
        guardandoPlanificacion={guardandoPlanificacion}
        splitTotalPalets={splitTotalPalets}
        splitPaletsPrimera={splitPaletsPrimera}
        splitPaletsSegunda={splitPaletsSegunda}
        splitDivisionInvalida={splitDivisionInvalida}
      />

      <PedidoDetalleDialog
        detallePedido={detallePedido}
        setDetallePedido={setDetallePedido}
        detallePedidoTitulo={detallePedidoTitulo}
        detallePedidoBlocks={detallePedidoBlocks}
        detallePedidoCambios={detallePedidoCambios}
        formatDateTime={formatDateTime}
        getFechaInicioPalet={getFechaInicioPalet}
        getFechaFinPalet={getFechaFinPalet}
      />

      <PlannerErrorSnackbar
        errorPlanificacion={errorPlanificacion}
        setErrorPlanificacion={setErrorPlanificacion}
      />
    </Box>
  )
}
